import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Divider,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Draggable from 'react-draggable';
import {
  AccountTree as TreeIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { buildCountyMap } from '../../utils/buildCountyMap';
import { useFlagMaps } from '../../hooks/useFlagMaps';
import {
  LEASE_VIEW_WARNING_THRESHOLD,
  LEASE_VIEW_WARNING_TITLE,
  LEASE_VIEW_WARNING_MESSAGE,
} from '../../constants/datasetLimits';
import { FlagIcon as FlagIconComponent } from '../shared/FlagIcon';

// Draggable Paper component for dialogs
const DraggablePaper = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Disable dragging on mobile to prevent touch event interference
  if (isMobile) {
    return <Paper {...props} ref={ref} />;
  }
  
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], button, [role="button"], [class*="MuiIconButton-root"]'}
    >
      <Paper {...props} ref={ref} />
    </Draggable>
  );
});
DraggablePaper.displayName = 'DraggablePaper';

const RelationalChart = ({ searchResults, searchParams, resultsFilter, forceViewMode, onClearMapSelection, onRegisterClearCallback, onZoomToData, onRegisterNavigateToLeaseInSummary, navigateToLeaseInMapRef }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Shared styling constants
  const cardPaperSx = { p: 1, minWidth: 160, border: '1px solid #e0e0e0', borderRadius: 1 };
  const emptyStateSx = { display: 'flex', alignItems: 'center', color: 'success.main' };
  const dividerSx = { my: 1, backgroundColor: '#e0e0e0' };
  const cardHeaderSx = { display: 'flex', alignItems: 'center', mb: 0.5 };
  
  // Well Group color scheme (matching WellsCharts)
  const wellGroupColors = {
    'ACTIVE': '#4caf50', 'PERMIT': '#607d8b', 'SHUT IN': '#ffc107',
    'TA': '#2196f3', 'PARTIAL PLUG': '#ff9800', 'P&A': '#8d6e63', 'DRY HOLE': '#e91e63'
  };
  
  // Helper components (defined inside to access theme/context if needed)
  const EmptyState = ({ message }) => (
    <Box sx={emptyStateSx}>
      <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
      <Typography variant="caption">{message}</Typography>
    </Box>
  );
  
  const FlaggedMetric = ({ label, value, flagCount, hasFlag, variant = 'caption', display = 'block', sx = {} }) => {
    const showFlag = hasFlag !== undefined ? hasFlag : (flagCount > 0);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant={variant} display={display} sx={{ flex: 1, ...sx }}>{label}: {value}</Typography>
        {showFlag && (flagCount !== undefined ? <FlagIconComponent count={flagCount} /> : <FlagIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />)}
      </Box>
    );
  };
  
  const MetricText = ({ label, value, display = 'block', sx = {} }) => (
    <Typography variant="caption" display={display} sx={sx}>{label}: {value}</Typography>
  );
  
  // State for expanded counties (still used for county expansion)
  const [expandedCounties, setExpandedCounties] = useState(new Set());
  
  // State for view mode (Summary, County, Lease)
  const [viewMode, setViewMode] = useState('summary');
  
  // State for confirmation dialog when switching to BY LEASE with large number of leases
  const [leaseViewConfirmOpen, setLeaseViewConfirmOpen] = useState(false);
  const [pendingViewMode, setPendingViewMode] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null); // { leaseKey, lease } for map -> summary navigation
  
  // State for sorting options
  const [countySortBy, setCountySortBy] = useState('Flags High to Low'); // 'A-Z', 'Z-A', 'Leases High to Low', 'Leases Low to High', 'Wells High to Low', 'Wells Low to High', 'Flags High to Low', 'Flags Low to High'
  const [leaseSortBy, setLeaseSortBy] = useState('Flags High to Low'); // 'A-Z', 'Z-A', 'Wells High to Low', 'Wells Low to High', 'Flags High to Low', 'Flags Low to High'
  
  // State for about dialog
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  
  // State for popup dialogs (multiple lease cards - draggable)
  const [openLeaseCards, setOpenLeaseCards] = useState(new Map()); // Map<leaseKey, { leaseData, county, position, size }>
  const [resizingLeaseKey, setResizingLeaseKey] = useState(null); // Track which popup is being resized
  const resizeStartPosRef = useRef({ x: 0, y: 0 });
  const resizeStartSizeRef = useRef({ width: 0, height: 0 });

  // Selection state for filtering (only in By Lease view)
  const [selectedLeaseKeys, setSelectedLeaseKeys] = useState(new Set());
  
  // State for selection dialog visibility
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);

  // Note: We do NOT sync selectedLeaseKeys with resultsFilter.selectedLeaseKeys
  // because selectedLeaseKeys represents the current UI selection (checkboxes),
  // while resultsFilter.selectedLeaseKeys represents the active filter.
  // When a filter is active, we show it via resultsFilter.isFilterActive, not by checking boxes.

  // Toggle lease selection
  const toggleLeaseSelection = (leaseKey) => {
    setSelectedLeaseKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leaseKey)) {
        newSet.delete(leaseKey);
      } else {
        newSet.add(leaseKey);
      }
      return newSet;
    });
  };

  // Register clear callback with parent
  useEffect(() => {
    if (onRegisterClearCallback) {
      onRegisterClearCallback(() => {
        setSelectedLeaseKeys(new Set());
        setSelectionDialogOpen(false);
      });
    }
  }, [onRegisterClearCallback]);

  // Helper function to check if well is inactive (matches WellsTab.js and WellsCharts.js logic)
  // Works with both raw well data and processed well objects
  const isInactiveWell = (well) => {
    // Handle both raw well data and processed well objects
    const plugDate = well['Plug Date'] || well.plug_date || well.plugged_date || well.plugDate || null;
    const wellGroup = well['Well Group'] || well.well_group || well.well_status || well.status || well.wellGroup || '';
    const inactiveYears = well.inactive_years !== undefined ? well.inactive_years : (well.inactiveYears !== undefined ? well.inactiveYears : 0);
    const wellGroupUpper = wellGroup.toString().toUpperCase();
    
    // Must be unplugged
    const isUnplugged = plugDate === null || plugDate === undefined || plugDate === '';
    
    // Check if Active or Permit
    const isActiveOrPermit = wellGroupUpper === 'ACTIVE' || wellGroupUpper === 'PERMIT';
    
    // Inactive if:
    // 1. Unplugged AND not Active/Permit (standard inactive wells)
    // OR
    // 2. Active/Permit BUT has inactive_years > 0 (should be considered inactive)
    return (isUnplugged && !isActiveOrPermit) || (isActiveOrPermit && inactiveYears > 0);
  };


  // Apply filter
  const handleApplyFilter = () => {
    if (resultsFilter && selectedLeaseKeys.size > 0) {
      resultsFilter.setLeaseKeys(selectedLeaseKeys);
      // Clear selection after applying filter
      setSelectedLeaseKeys(new Set());
      // Close dialog after applying filter
      setSelectionDialogOpen(false);
      // Also clear Map selection and close its dialog
      if (onClearMapSelection) {
        onClearMapSelection();
      }
    }
  };

  // Clear selection (only clears checkboxes, not the filter)
  const handleClearSelection = () => {
    setSelectedLeaseKeys(new Set());
  };

  // Close selection dialog and clear checkboxes
  const handleCloseSelectionDialog = () => {
    setSelectionDialogOpen(false);
    setSelectedLeaseKeys(new Set());
  };

  // Auto-open/close dialog when selection changes
  // This should trigger when user actively selects checkboxes, even if a filter is already active
  useEffect(() => {
    if (viewMode === 'lease') {
      // Auto-open if:
      // 1. User has made selections (selectedLeaseKeys.size > 0)
      // 2. Dialog is not already open
      if (selectedLeaseKeys.size > 0 && !selectionDialogOpen) {
        setSelectionDialogOpen(true);
      }
      // Auto-close if:
      // 1. All selections are cleared (selectedLeaseKeys.size === 0)
      // 2. Dialog is currently open
      else if (selectedLeaseKeys.size === 0 && selectionDialogOpen) {
        setSelectionDialogOpen(false);
      }
    }
  }, [selectedLeaseKeys.size, viewMode, selectionDialogOpen]);

  // Determine which tables are included in the search
  const includedTables = useMemo(() => {
    if (!searchParams) return { wells: true, production: true, inspections: true, violations: true, severances: true, spills: true };
    
    return {
      wells: searchParams.search_wells || false,
      production: searchParams.search_production || false,
      inspections: searchParams.search_inspections || false,
      violations: searchParams.search_violations || false,
      severances: searchParams.search_severances || false,
      spills: searchParams.search_spills || false,
    };
  }, [searchParams]);

  // Helper function to get visible cards in order
  const getVisibleCards = () => {
    const cardOrder = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills'];
    return cardOrder.filter(card => includedTables[card]);
  };

  // Helper function to render lease card content (used in Dialog)
  const renderLeaseCardContent = (leaseData, flags) => {
    const visibleCards = getVisibleCards();
    
    return (
      <Box sx={{ mb: 3 }}>
        {/* Flow Boxes */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {visibleCards.map((cardType, index) => {
            const isLastCard = index === visibleCards.length - 1;
            
            return (
              <React.Fragment key={cardType}>
                {/* Wells Box */}
                {cardType === 'wells' && (
                  <Paper sx={cardPaperSx}>
                    <Box sx={cardHeaderSx}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#8d6e63' }}>
                        Wells
                      </Typography>
                    </Box>
                    {leaseData.wells.length > 0 ? (
                      <Box>
                        {(() => {
                          // Calculate statistics
                          const wellRecords = leaseData.wells.length;
                          const uniqueAPIs = new Set(leaseData.wells.map(well => well.apiNumber).filter(api => api)).size;
                          const totalARO = leaseData.wells.reduce((sum, well) => {
                            const aro = (well.pluggingCost !== null && well.pluggingCost !== undefined && 
                                         typeof well.pluggingCost === 'number' && !isNaN(well.pluggingCost) && well.pluggingCost > 0) 
                                         ? well.pluggingCost : 0;
                            return sum + aro;
                          }, 0);
                          
                          // Format ARO
                          const formatARO = (aro) => {
                            if (aro === 0) return null;
                            return `Est ARO: $${aro.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                          };
                          
                          return (
                            <>
                              <Typography variant="caption" display="block">
                                Well Records: {wellRecords.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Unique APIs: {uniqueAPIs.toLocaleString()}
                              </Typography>
                              {formatARO(totalARO) && (
                                <Typography variant="caption" display="block">
                                  {formatARO(totalARO)}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                        <Divider sx={dividerSx} />
                        {/* Sort wells by API number and display all */}
                        {(() => {
                          const sortedWells = [...leaseData.wells].sort((a, b) => 
                            (a.apiNumber || '').localeCompare(b.apiNumber || '')
                          );
                          
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {sortedWells.map((well, index) => {
                                const backgroundColor = wellGroupColors[well.wellGroup] || '#e0e0e0';
                                const textColor = well.wellGroup === 'SHUT IN' ? '#000000' : '#ffffff';
                                
                                // Uppercase "Unknown Well" for display consistency
                                const displayWellName = well.wellName === 'Unknown Well' ? 'UNKNOWN WELL' : well.wellName;
                                
                                // Check if well qualifies under Risk Wells logic using shared helper
                                const isInactive = isInactiveWell(well);
                                const inactiveYears = well.inactiveYears;
                                
                                // Get ARO (plugging cost) for this well
                                // The pluggingCost is now included in the well object from buildCountyMap
                                const wellARO = (well.pluggingCost !== null && well.pluggingCost !== undefined && 
                                               typeof well.pluggingCost === 'number' && !isNaN(well.pluggingCost) && well.pluggingCost > 0) 
                                               ? well.pluggingCost : null;
                                const aroDisplay = wellARO ? `ARO $${wellARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '';
                                
                                // Build label with inactive years if qualifies (inactive years before ARO)
                                let label = `${displayWellName} | ${well.apiNumber} | ${well.wellGroup}`;
                                const isInactiveOver5Years = isInactive && inactiveYears > 5;
                                if (isInactive) {
                                  if (inactiveYears === null || inactiveYears === undefined) {
                                    label += ` | UNKNOWN INACT YRS`;
                                  } else if (inactiveYears > 0) {
                                    label += ` | ${inactiveYears.toFixed(1)} INACT YRS`;
                                  }
                                }
                                // Add ARO at the end
                                if (aroDisplay) {
                                  label += ` | ${aroDisplay}`;
                                }
                                
                                return (
                                  <Box key={index} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                    <Chip 
                                      label={label}
                                      size="small"
                                      variant="filled"
                                      sx={{ 
                                        fontSize: '0.65rem',
                                        backgroundColor: backgroundColor,
                                        color: textColor,
                                        '& .MuiChip-label': {
                                          color: textColor
                                        }
                                      }}
                                    />
                                    {isInactive && flags.inactiveWells && (
                                      <FlagIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />
                                    )}
                                    {isInactiveOver5Years && flags.inactiveWellsOver5Years && (
                                      <FlagIcon sx={{ fontSize: '0.75rem', color: 'error.main', ml: isInactive && flags.inactiveWells ? 0 : 0 }} />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })()}
                      </Box>
                    ) : (
                      <EmptyState message="No wells data" />
                    )}
                  </Paper>
                )}

                {/* Production Box */}
                {cardType === 'production' && (
                  <Paper sx={cardPaperSx}>
                    <Box sx={cardHeaderSx}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        Production (Avg)
                      </Typography>
                    </Box>
                    {leaseData.production ? (
                      <Box>
                        <MetricText label="Oil" value={`${(leaseData.production.avgOilBblPerDay || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} bbl/d`} />
                        <MetricText label="Gas" value={`${(leaseData.production.avgGasMcfPerDay || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d`} />
                        <Divider sx={dividerSx} />
                        <MetricText label="Flaring" value={`${(leaseData.production.avgFlaringPerDay || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d`} />
                        <MetricText label="Fuel Gas Use" value={`${(leaseData.production.avgFuelGasPerDay || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d`} />
                        <Divider sx={dividerSx} />
                        <FlaggedMetric label="Flaring Intensity" value={`${(leaseData.production.avgFlaringIntensity || 0).toFixed(2)}%`} hasFlag={flags.flaringIntensity} />
                        <FlaggedMetric label="Fuel Gas Use Intensity" value={`${(leaseData.production.avgFuelGasIntensity || 0).toFixed(2)}%`} hasFlag={flags.fuelGasIntensity} />
                      </Box>
                    ) : (
                      <EmptyState message="No production data" />
                    )}
                  </Paper>
                )}

                {/* Inspections Box */}
                {cardType === 'inspections' && (
                  <Paper sx={cardPaperSx}>
                    <Box sx={cardHeaderSx}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        Inspections
                      </Typography>
                    </Box>
                    {leaseData.inspections ? (
                      <Box>
                        <MetricText label="Count" value={(leaseData.inspections.count || 0).toLocaleString()} />
                        <FlaggedMetric label="Compliance" value={`${(leaseData.inspections.complianceRate || 0).toFixed(1)}%`} hasFlag={flags.inspectionsCompliance} />
                      </Box>
                    ) : (
                      <EmptyState message="No inspections data" />
                    )}
                  </Paper>
                )}

                {/* Violations Box */}
                {cardType === 'violations' && (
                  <Paper sx={cardPaperSx}>
                    <Box sx={cardHeaderSx}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        Violations
                      </Typography>
                    </Box>
                    {leaseData.violations ? (
                      <Box>
                        <FlaggedMetric label="Count" value={(leaseData.violations.count || 0).toLocaleString()} hasFlag={flags.violationsCount} />
                        <FlaggedMetric label="Major" value={(leaseData.violations.majorCount || 0).toLocaleString()} hasFlag={flags.violationsMajor} />
                        <Divider sx={dividerSx} />
                        <FlaggedMetric label="Reinspection Compliance" value={leaseData.violations.totalReinspections === 0 ? 'N/A' : `${(leaseData.violations.reinspectionComplianceRate || 0).toFixed(1)}%`} hasFlag={flags.violationsReinspection && leaseData.violations.totalReinspections > 0} />
                        <MetricText label="Not Compliant/Not Reinspected" value={(leaseData.violations.notCompliantOrNotReinspected || 0).toLocaleString()} />
                        <Divider sx={dividerSx} />
                        <MetricText label="Leading Cause" value={`${leaseData.violations.leadingCause || 'Unknown'}${leaseData.violations.leadingCausePercentage ? ` (${leaseData.violations.leadingCausePercentage.toFixed(1)}%)` : ''}`} />
                      </Box>
                    ) : (
                      <EmptyState message="No violations data" />
                    )}
                  </Paper>
                )}

                {/* Severances Box */}
                {cardType === 'severances' && (
                  <Paper sx={cardPaperSx}>
                    <Box sx={cardHeaderSx}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#607d8b' }}>
                        Severances
                      </Typography>
                    </Box>
                    {leaseData.severances ? (
                      <Box>
                        <FlaggedMetric label="Count" value={(leaseData.severances.count || 0).toLocaleString()} hasFlag={flags.severancesCount} />
                        <FlaggedMetric label="Resolved" value={`${(leaseData.severances.resolvedRate || 0).toFixed(1)}%`} hasFlag={flags.severancesResolved} />
                        <MetricText label="Avg Days to Resolve" value={(leaseData.severances.avgDaysToResolve || 0).toFixed(0)} />
                        <Divider sx={dividerSx} />
                        <MetricText label="Leading Cause" value={`${leaseData.severances.leadingCause || 'Unknown'}${leaseData.severances.leadingCausePercentage ? ` (${leaseData.severances.leadingCausePercentage}%)` : ''}`} />
                      </Box>
                    ) : (
                      <EmptyState message="No severances data" />
                    )}
                  </Paper>
                )}

                {/* Spills Box */}
                {cardType === 'spills' && (
                  <Paper sx={cardPaperSx}>
                    <Box sx={cardHeaderSx}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.dark' }}>
                        Spills
                      </Typography>
                    </Box>
                    {leaseData.spills ? (
                      <Box>
                        <FlaggedMetric label="Count" value={(leaseData.spills.count || 0).toLocaleString()} hasFlag={flags.spillsCount} sx={{ color: 'text.primary' }} />
                        <FlaggedMetric label="Water Impact Count" value={(leaseData.spills.waterImpactCount || 0).toLocaleString()} hasFlag={flags.spillsWaterImpact} sx={{ color: 'text.primary' }} />
                        <MetricText label="Avg Spill" value={`${Math.round(leaseData.spills.avgSpillVolume || 0).toLocaleString()} bbls`} sx={{ color: 'text.primary' }} />
                        <MetricText label="Total Reported Spilled" value={`${Math.round(leaseData.spills.totalReportedVolume || 0).toLocaleString()} bbls`} sx={{ color: 'text.primary' }} />
                        <Divider sx={dividerSx} />
                        <MetricText label="Volume Recovered" value={`${(leaseData.spills.recoveryRate || 0).toFixed(1)}%`} sx={{ color: 'text.primary' }} />
                        <MetricText label="Volume Lost" value={`${(leaseData.spills.lossRate || 0).toFixed(1)}%`} sx={{ color: 'text.primary' }} />
                        <Divider sx={dividerSx} />
                        <MetricText label="Leading Cause" value={`${leaseData.spills.leadingCause || 'Unknown'}${leaseData.spills.leadingCausePercentage ? ` (${leaseData.spills.leadingCausePercentage}%)` : ''}`} sx={{ color: 'text.primary' }} />
                        <MetricText label="Top Occurrence Facility" value={`${leaseData.spills.topFacility || 'Unknown'}${leaseData.spills.topFacilityPercentage ? ` (${leaseData.spills.topFacilityPercentage}%)` : ''}`} sx={{ color: 'text.primary' }} />
                        <MetricText label="Highest Liquid Spilled" value={`${leaseData.spills.topLiquidType || 'Unknown'}${leaseData.spills.topLiquidTypePercentage ? ` (${leaseData.spills.topLiquidTypePercentage}%)` : ''}`} sx={{ color: 'text.primary' }} />
                      </Box>
                    ) : (
                      <EmptyState message="No spills data" />
                    )}
                  </Paper>
                )}

                {/* Arrow - only show if not the last card */}
                {!isLastCard && (
                  <Typography variant="h4" sx={{ color: 'text.secondary' }}>→</Typography>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Handle View Lease Card - opens popup dialog (supports multiple)
  const handleViewLeaseCard = useCallback((leaseKey, leaseData, county) => {
    setOpenLeaseCards(prev => {
      const newMap = new Map(prev);
      const count = newMap.size;
      // Stagger position for multiple dialogs
      // Default size: 800px width, auto height
      newMap.set(leaseKey, {
        leaseData,
        county,
        position: { x: count * 50, y: count * 50 },
        size: { width: 800, height: 'auto' }
      });
      return newMap;
    });
  }, []);

  // Function to close a specific lease card
  const handleCloseLeaseCard = useCallback((leaseKey) => {
    setOpenLeaseCards(prev => {
      const newMap = new Map(prev);
      newMap.delete(leaseKey);
      return newMap;
    });
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback((e, leaseKey) => {
    e.preventDefault();
    e.stopPropagation();
    const card = openLeaseCards.get(leaseKey);
    if (card) {
      setResizingLeaseKey(leaseKey);
      resizeStartPosRef.current = { x: e.clientX, y: e.clientY };
      resizeStartSizeRef.current = { 
        width: card.size?.width || 800, 
        height: card.size?.height === 'auto' ? 600 : (card.size?.height || 600)
      };
    }
  }, [openLeaseCards]);

  // Handle resize move
  useEffect(() => {
    if (!resizingLeaseKey) return;

      const handleMouseMove = (e) => {
        const deltaX = e.clientX - resizeStartPosRef.current.x;
        const deltaY = e.clientY - resizeStartPosRef.current.y;
        
        setOpenLeaseCards(prev => {
          const newMap = new Map(prev);
          const card = newMap.get(resizingLeaseKey);
          if (card) {
            const newWidth = Math.max(400, Math.min(1400, resizeStartSizeRef.current.width + deltaX));
            const newHeight = Math.max(300, Math.min(window.innerHeight * 0.9, resizeStartSizeRef.current.height + deltaY));
          newMap.set(resizingLeaseKey, {
            ...card,
            size: { width: newWidth, height: newHeight }
          });
        }
        return newMap;
      });
    };

    const handleMouseUp = () => {
      setResizingLeaseKey(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingLeaseKey]);
  
  // Toggle county expansion
  const toggleCountyExpansion = (county) => {
    const newExpanded = new Set(expandedCounties);
    if (newExpanded.has(county)) {
      newExpanded.delete(county);
    } else {
      newExpanded.add(county);
    }
    setExpandedCounties(newExpanded);
  };
  
  // Helper function to format lease number for display
  // Returns District-LeaseNumber for normal leases, or District-00000 for unknown leases
  const formatLeaseNumberForDisplay = (district, leaseNumber) => {
    const dist = district || '00';
    const num = leaseNumber ? (typeof leaseNumber === 'string' ? leaseNumber.trim() : String(leaseNumber)) : null;
    
    if (num && num !== 'Unknown Lease' && num !== '') {
      // Normal case: District-LeaseNumber
      return `${String(dist).padStart(2, '0')}-${num}`.toUpperCase();
    } else {
      // Unknown lease: District-00000
      return `${String(dist).padStart(2, '0')}-00000`;
    }
  };

  // Process data to create relational structure with aggregated stats
  const relationalData = useMemo(() => {
    if (!searchResults?.data) return null;

    // Use shared utility to build countyMap
    const { countyMap, countyCounts } = buildCountyMap(searchResults, includedTables);
    
    return { countyMap, countyCounts };
  }, [searchResults, includedTables]);
  
  // Extract countyMap and countyCounts from relationalData
  const countyMap = relationalData?.countyMap || null;
  const countyCounts = useMemo(() => {
    return relationalData?.countyCounts || new Map();
  }, [relationalData]);

  // Calculate total number of leases across all counties
  const totalLeaseCount = useMemo(() => {
    if (!countyMap) return 0;
    let total = 0;
    countyMap.forEach((leases) => {
      total += leases.size;
    });
    return total;
  }, [countyMap]);

  // Extract navigation logic into a reusable function (single source of truth)
  const executeLeaseNavigation = useCallback((leaseKey, lease) => {
    // Optionally set view mode to lease if not already
    if (viewMode !== 'lease') {
      setViewMode('lease');
    }
    
    // Find the county containing this lease
    let targetCounty = null;
    let leaseData = null;
    
    // If lease object has county property (from county-specific marker), use it
    if (lease && lease.county) {
      targetCounty = lease.county;
      // Find the lease data from countyMap for this specific county
      if (countyMap && countyMap.has(targetCounty)) {
        const countyLeases = countyMap.get(targetCounty);
        if (countyLeases.has(leaseKey)) {
          leaseData = countyLeases.get(leaseKey);
        }
      }
    } else if (countyMap) {
      // Fallback: search all counties for the lease
      for (const [county, leases] of countyMap.entries()) {
        if (leases.has(leaseKey)) {
          targetCounty = county;
          leaseData = leases.get(leaseKey);
          break;
        }
      }
    }
    
    // If we have the lease data, open the popup dialog
    if (targetCounty && leaseData) {
      handleViewLeaseCard(leaseKey, leaseData, targetCounty);
    } else if (lease) {
      // Fallback: if lease object is passed from map, use it directly
      handleViewLeaseCard(leaseKey, lease, targetCounty || 'Unknown County');
    }
  }, [viewMode, countyMap, handleViewLeaseCard]);

  // Register navigation callback for Map -> Summary navigation (after countyMap is defined)
  useEffect(() => {
    if (onRegisterNavigateToLeaseInSummary) {
      onRegisterNavigateToLeaseInSummary((leaseKey, lease) => {
        // Check if switching to BY LEASE view with threshold+ leases
        if (viewMode !== 'lease' && totalLeaseCount >= LEASE_VIEW_WARNING_THRESHOLD) {
          // Show confirmation dialog and store navigation info
          setPendingNavigation({ leaseKey, lease });
          setLeaseViewConfirmOpen(true);
        } else {
          // Execute navigation immediately
          executeLeaseNavigation(leaseKey, lease);
        }
      });
    }
  }, [onRegisterNavigateToLeaseInSummary, viewMode, totalLeaseCount, executeLeaseNavigation]);

  // Function to navigate from Summary to Map for a specific lease
  const handleNavigateToLeaseInMap = (leaseKey, county) => {
    if (navigateToLeaseInMapRef && navigateToLeaseInMapRef.current) {
      navigateToLeaseInMapRef.current(leaseKey, county);
    }
  };

  // Use the hook to get flag maps (reuses countyMap to avoid duplication)
  const { countyFlagsMap, redFlagsCache } = useFlagMaps(
    searchResults,
    searchParams,
    countyMap // Pass the pre-built countyMap
  );

  // Handle view mode change with confirmation for large number of leases
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      // Check if switching to BY LEASE view with threshold+ leases
      if (newViewMode === 'lease' && totalLeaseCount >= LEASE_VIEW_WARNING_THRESHOLD) {
        // Show confirmation dialog
        setPendingViewMode(newViewMode);
        setLeaseViewConfirmOpen(true);
        } else {
        // Switch immediately for other cases
        setViewMode(newViewMode);
      }
    }
  };

  // Handle confirmation dialog - proceed with switching to BY LEASE
  const handleConfirmLeaseView = () => {
    // Set view mode if pending
    if (pendingViewMode) {
      setViewMode(pendingViewMode);
      setPendingViewMode(null);
    }
    
    // Execute pending navigation if any (from map -> summary)
    if (pendingNavigation) {
      const { leaseKey, lease } = pendingNavigation;
      setPendingNavigation(null);
      executeLeaseNavigation(leaseKey, lease);
    }
    
    setLeaseViewConfirmOpen(false);
  };

  // Handle confirmation dialog - cancel switching to BY LEASE
  const handleCancelLeaseView = () => {
    setPendingViewMode(null);
    setPendingNavigation(null);
    setLeaseViewConfirmOpen(false);
  };

  // Handle external view mode control (e.g., from Filter Search Results button)
  useEffect(() => {
    if (forceViewMode && forceViewMode !== viewMode) {
      // Check if switching to BY LEASE view with threshold+ leases
      if (forceViewMode === 'lease' && totalLeaseCount >= LEASE_VIEW_WARNING_THRESHOLD) {
        // Show confirmation dialog
        setPendingViewMode(forceViewMode);
        setLeaseViewConfirmOpen(true);
        } else {
        // Switch immediately for other cases
        setViewMode(forceViewMode);
      }
    }
  }, [forceViewMode, viewMode, totalLeaseCount]);
  
  // Memoize sorted counties for performance
  const sortedCountiesMemo = useMemo(() => {
    if (!countyMap) return [];
    return Array.from(countyMap.entries()).sort(([countyA, leasesA], [countyB, leasesB]) => {
      let comparison = 0;
      
      switch (countySortBy) {
        case 'A-Z':
          comparison = countyA.localeCompare(countyB);
          break;
        case 'Z-A':
          comparison = countyB.localeCompare(countyA);
          break;
        case 'Leases High to Low':
          comparison = leasesB.size - leasesA.size;
          break;
        case 'Leases Low to High':
          comparison = leasesA.size - leasesB.size;
          break;
        case 'Wells High to Low':
          const wellCountA = countyCounts.get(countyA)?.wellCount || 0;
          const wellCountB = countyCounts.get(countyB)?.wellCount || 0;
          comparison = wellCountB - wellCountA;
          break;
        case 'Wells Low to High':
          const wellCountA2 = countyCounts.get(countyA)?.wellCount || 0;
          const wellCountB2 = countyCounts.get(countyB)?.wellCount || 0;
          comparison = wellCountA2 - wellCountB2;
          break;
        case 'Flags High to Low':
          const flagCountA = countyFlagsMap.get(countyA.toUpperCase()) || 0;
          const flagCountB = countyFlagsMap.get(countyB.toUpperCase()) || 0;
          comparison = flagCountB - flagCountA;
          break;
        case 'Flags Low to High':
          const flagCountA2 = countyFlagsMap.get(countyA.toUpperCase()) || 0;
          const flagCountB2 = countyFlagsMap.get(countyB.toUpperCase()) || 0;
          comparison = flagCountA2 - flagCountB2;
          break;
        default:
          comparison = countyA.localeCompare(countyB);
      }
      
      return comparison;
    });
  }, [countyMap, countyCounts, countySortBy, countyFlagsMap]);


  // Calculate flag counts per specific metric for SUMMARY view (across all leases)
  const summaryFlagCounts = useMemo(() => {
    if (!countyMap || !redFlagsCache) {
      return {
        wellsInactive: 0,
        wellsInactiveOver5: 0,
        productionFlaring: 0,
        productionFuelGas: 0,
        inspectionsCompliance: 0,
        violationsCount: 0,
        violationsMajor: 0,
        violationsReinspection: 0,
        severancesCount: 0,
        severancesResolved: 0,
        spillsCount: 0,
        spillsWaterImpact: 0
      };
    }

    let wellsInactive = 0;
    let wellsInactiveOver5 = 0;
    let productionFlaring = 0;
    let productionFuelGas = 0;
    let inspectionsCompliance = 0;
    let violationsCount = 0;
    let violationsMajor = 0;
    let violationsReinspection = 0;
    let severancesCount = 0;
    let severancesResolved = 0;
    let spillsCount = 0;
    let spillsWaterImpact = 0;

    countyMap.forEach((leases, county) => {
      leases.forEach((leaseData, leaseKey) => {
        // Use county-lease key for accurate flag counts
        const countyLeaseKey = `${county}-${leaseKey}`;
        const cached = redFlagsCache.get(countyLeaseKey);
        if (cached) {
          const { flags } = cached;
          
          // Wells flags: count leases with inactive wells
          if (flags.inactiveWells) {
            // Count the number of inactive wells (not just if flag exists)
            if (leaseData.wells && leaseData.wells.length > 0) {
              const inactiveWells = leaseData.wells.filter(well => isInactiveWell(well));
              wellsInactive += inactiveWells.length;
            }
          }
          
          if (flags.inactiveWellsOver5Years) {
            if (leaseData.wells && leaseData.wells.length > 0) {
              const inactiveWells = leaseData.wells.filter(well => isInactiveWell(well));
              const inactiveOver5Years = inactiveWells.filter(well => {
                const inactiveYears = well.inactiveYears !== undefined ? well.inactiveYears : (well.inactive_years || 0);
                return inactiveYears > 5;
              });
              wellsInactiveOver5 += inactiveOver5Years.length;
            }
          }
          
          // Production flags: count leases with flags
          if (flags.flaringIntensity) productionFlaring += 1;
          if (flags.fuelGasIntensity) productionFuelGas += 1;
          
          // Inspections flags
          if (flags.inspectionsCompliance) inspectionsCompliance += 1;
          
          // Violations flags
          if (flags.violationsCount) violationsCount += 1;
          if (flags.violationsMajor) violationsMajor += 1;
          if (flags.violationsReinspection) violationsReinspection += 1;
          
          // Severances flags
          if (flags.severancesCount) severancesCount += 1;
          if (flags.severancesResolved) severancesResolved += 1;
          
          // Spills flags
          if (flags.spillsCount) spillsCount += 1;
          if (flags.spillsWaterImpact) spillsWaterImpact += 1;
        }
      });
    });

    return {
      wellsInactive,
      wellsInactiveOver5,
      productionFlaring,
      productionFuelGas,
      inspectionsCompliance,
      violationsCount,
      violationsMajor,
      violationsReinspection,
      severancesCount,
      severancesResolved,
      spillsCount,
      spillsWaterImpact
    };
  }, [countyMap, redFlagsCache]);

  // Calculate selection counts
  const getSelectionCounts = useMemo(() => {
    if (!countyMap || selectedLeaseKeys.size === 0) {
      return { counties: 0, leases: 0 };
    }

    // Count unique counties from selected leases
    const countiesSet = new Set();
    countyMap.forEach((leases, county) => {
      leases.forEach((leaseData, leaseKey) => {
        if (selectedLeaseKeys.has(leaseKey)) {
          countiesSet.add(county);
        }
      });
    });

    return {
      counties: countiesSet.size,
      leases: selectedLeaseKeys.size
    };
  }, [countyMap, selectedLeaseKeys]);


  // Early return if no data (must be after all hooks)
  if (!relationalData || !countyMap || countyMap.size === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" color="text.secondary">
          Summary analysis requires the Wells table to be included in search.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TreeIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Report Cards
            </Typography>
          </Box>
          <Tooltip title="About Report Cards" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setAboutDialogOpen(true)}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Selection Dialog - Show when open and in By Lease view */}
        {viewMode === 'lease' && selectionDialogOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 80,
              right: 16,
              zIndex: 1000,
              minWidth: 200,
            }}
          >
            <Paper
              sx={{
                p: 2,
                boxShadow: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Selection
                </Typography>
                <Tooltip title="Close Selection" arrow>
                  <IconButton
                    size="small"
                    onClick={handleCloseSelectionDialog}
                    sx={{
                      padding: 0.5,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {getSelectionCounts.leases > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  {getSelectionCounts.counties} Count{getSelectionCounts.counties !== 1 ? 'ies' : 'y'}, {getSelectionCounts.leases} Lease{getSelectionCounts.leases !== 1 ? 's' : ''} Selected
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilter}
                disabled={selectedLeaseKeys.size === 0}
                color="primary"
                fullWidth
              >
                Apply Filter
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearSelection}
                disabled={selectedLeaseKeys.size === 0}
                color="primary"
                fullWidth
              >
                Clear Selection
              </Button>
            </Paper>
          </Box>
        )}

        {/* View Mode Toggle */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }
            }}
          >
            <ToggleButton value="summary" aria-label="summary view">
              All
            </ToggleButton>
            <ToggleButton value="county" aria-label="county view">
              By County
            </ToggleButton>
            <ToggleButton value="lease" aria-label="lease view">
              By Lease
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Confirmation Dialog for BY LEASE view with large number of leases */}
        <Dialog
          open={leaseViewConfirmOpen}
          onClose={handleCancelLeaseView}
          aria-labelledby="lease-view-confirm-title"
          aria-describedby="lease-view-confirm-description"
        >
          <DialogTitle id="lease-view-confirm-title">
            {LEASE_VIEW_WARNING_TITLE}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="lease-view-confirm-description">
              {LEASE_VIEW_WARNING_MESSAGE}
              <br /><br />
              Are you sure you want to continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLeaseView} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmLeaseView} color="primary" variant="contained" autoFocus>
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Summary Data Flow Analysis */}
        {/* Overall Asset/Search Total Summary */}
          <Box sx={{ mb: 3 }}>
            {(() => {
              // Calculate overall totals using raw data (matching Findings card approach)
              const totalCounties = countyMap?.size || 0;
              
              // Deduplicate leases by District-Lease# across all counties
              const uniqueLeaseKeys = new Set();
              if (countyMap) {
                Array.from(countyMap.values()).forEach(leases => {
                  Array.from(leases.keys()).forEach(leaseKey => {
                    uniqueLeaseKeys.add(leaseKey);
                  });
                });
              }
              const totalLeases = uniqueLeaseKeys.size;
              
              // Use pre-computed county counts for better performance
              let totalWells = 0;
              if (countyCounts) {
                countyCounts.forEach((counts) => {
                  totalWells += counts.wellCount || 0;
                });
              }
              
              // Calculate unique APIs from all wells
              const allWellsData = searchResults.data.wells || [];
              const uniqueAPIs = new Set();
              allWellsData.forEach(well => {
                const apiNumber = well['API Number'] || well.api_number || well.api || well.apiNumber;
                if (apiNumber && apiNumber !== 'Unknown API' && apiNumber !== '') {
                  uniqueAPIs.add(String(apiNumber).trim());
                }
              });
              const totalUniqueAPIs = uniqueAPIs.size;
              
              // Calculate inactive wells statistics (matching WellsTab.js logic)
              
              const allInactiveWells = allWellsData.filter(well => isInactiveWell(well));
              const inactiveOver5Years = allInactiveWells.filter(well => {
                const inactiveYears = well.inactive_years || 0;
                return inactiveYears > 5;
              });
              
              // Calculate average inactive years
              const avgInactiveYears = allInactiveWells.length > 0
                ? allInactiveWells.reduce((sum, well) => sum + (well.inactive_years || 0), 0) / allInactiveWells.length
                : 0;
              
              const avgInactiveOver5Years = inactiveOver5Years.length > 0
                ? inactiveOver5Years.reduce((sum, well) => sum + (well.inactive_years || 0), 0) / inactiveOver5Years.length
                : 0;
              
              // Helper function to get plugging cost (ARO) for a well
              const getPluggingCost = (well) => {
                const pluggingCost = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
                if (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
                    typeof pluggingCost === 'number' && !isNaN(pluggingCost)) {
                  return pluggingCost;
                }
                return 0;
              };
              
              // Calculate ARO for inactive wells
              const inactiveTotalARO = allInactiveWells.reduce((sum, well) => sum + getPluggingCost(well), 0);
              const inactiveOver5YearsARO = inactiveOver5Years.reduce((sum, well) => sum + getPluggingCost(well), 0);
              
              // Calculate total ARO for all wells
              const totalARO = allWellsData.reduce((sum, well) => sum + getPluggingCost(well), 0);
              
              // Format ARO in regular dollar format (for inactive lines)
              const formatARO = (aro) => {
                if (aro === 0) return null;
                return `ARO $${aro.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
              };
              
              // Use raw data for counts (more accurate than summing processed counts)
              const totalViolations = (searchResults.data.violations || []).length;
              const totalSpills = (searchResults.data.spills || []).length;
              const totalSeverances = (searchResults.data.severances || []).length;
              const totalInspections = (searchResults.data.inspections || []).length;
              
              // Calculate overall aggregated production data - use raw production data like Findings card
              const allProductionData = searchResults.data.production || [];
              const totalProduction = allProductionData.length; // Record count for condition check
              
              // Calculate totals from raw production records (matching ProductionTab logic)
              const overallTotalOilBbl = allProductionData.reduce((sum, record) => {
                const oil = parseFloat(record['Oil BBL'] || record.oil_bbl || 0);
                return sum + oil;
              }, 0);
              
              const overallTotalGasMcf = allProductionData.reduce((sum, record) => {
                const gas = parseFloat(record['Gas MCF'] || record.gas_mcf || 0);
                return sum + gas;
              }, 0);
              
              const overallTotalFlareGasMcf = allProductionData.reduce((sum, record) => {
                const flaring = parseFloat(record['Flare Gas MCF'] || record.flare_gas_mcf || 0);
                return sum + flaring;
              }, 0);
              
              const overallTotalFuelGasMcf = allProductionData.reduce((sum, record) => {
                const fuelGas = parseFloat(record['Fuel Gas MCF'] || record.fuel_gas_mcf || 0);
                return sum + fuelGas;
              }, 0);
              
              // Calculate unique production months and total days (matching ProductionTab logic)
              const overallUniqueMonths = new Set(allProductionData.map(record => record['Production Month'] || record.production_month));
              const overallTotalDays = Array.from(overallUniqueMonths).reduce((sum, month) => {
                // Find first record for this month to get its days_month
                const firstRecord = allProductionData.find(record => (record['Production Month'] || record.production_month) === month);
                return sum + (parseFloat(firstRecord?.['Days in Month'] || firstRecord?.days_month) || 0);
              }, 0);
              
              // Calculate daily averages from aggregated totals
              const overallAvgOilBblPerDay = overallTotalDays > 0 ? overallTotalOilBbl / overallTotalDays : 0;
              const overallAvgGasMcfPerDay = overallTotalDays > 0 ? overallTotalGasMcf / overallTotalDays : 0;
              const overallAvgFlaringIntensity = overallTotalGasMcf > 0 ? (overallTotalFlareGasMcf / overallTotalGasMcf) * 100 : 0;
              const overallAvgFuelGasIntensity = overallTotalGasMcf > 0 ? (overallTotalFuelGasMcf / overallTotalGasMcf) * 100 : 0;
              
              // Calculate overall compliance rates using raw data (matching InspectionsTab logic)
              const allInspectionsData = searchResults.data.inspections || [];
              const complianceColumn = allInspectionsData[0]?.['Compliance'] !== undefined ? 'Compliance' :
                                    allInspectionsData[0]?.compliance !== undefined ? 'compliance' : null;
              
              let overallAvgComplianceRate = 0;
              if (complianceColumn) {
                const overallCompliantInspections = allInspectionsData.filter(record => {
                  const compliance = record[complianceColumn];
                  return compliance && compliance.toString().toUpperCase() === 'YES';
                }).length;
                overallAvgComplianceRate = allInspectionsData.length > 0 ? (overallCompliantInspections / allInspectionsData.length) * 100 : 0;
              }
              
              // Calculate overall violation data using raw data (matching ViolationsTab logic)
              const allViolationsData = searchResults.data.violations || [];
              
              // Calculate major violations count
              const majorViolationColumn = allViolationsData[0]?.['Major Violation'] !== undefined ? 'Major Violation' :
                                        allViolationsData[0]?.major_violation !== undefined ? 'major_violation' : null;
              
              let overallTotalMajorViolations = 0;
              if (majorViolationColumn) {
                overallTotalMajorViolations = allViolationsData.filter(record => {
                  const majorViolation = record[majorViolationColumn];
                  return majorViolation && majorViolation.toString().toUpperCase() === 'YES';
                }).length;
              }
              
              // Calculate reinspection compliance using raw data (matching ViolationsTab logic)
              const reinspectionColumn = allViolationsData[0]?.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
              
              let overallTotalReinspections = 0;
              let overallTotalCompliantReinspections = 0;
              let overallNotCompliantOrNotReinspected = 0;
              
              if (reinspectionColumn) {
                // Count reinspections: records where field is not empty and not "--"
                const reinspectionRecords = allViolationsData.filter(record => {
                  const reinspection = record[reinspectionColumn];
                  return reinspection && reinspection.toString().trim() !== '' && reinspection.toString().trim() !== '--';
                });
                
                overallTotalReinspections = reinspectionRecords.length;
                
                // Count compliant reinspections: records where field equals "Y"
                overallTotalCompliantReinspections = reinspectionRecords.filter(record => {
                  const compliance = record[reinspectionColumn];
                  return compliance && compliance.toString().toUpperCase() === 'Y';
                }).length;
                
                // Not Compliant/Not Reinspected = Total violations - Total reinspections
                overallNotCompliantOrNotReinspected = allViolationsData.length - overallTotalReinspections;
              }
              
              const overallAvgReinspectionComplianceRate = overallTotalReinspections > 0 ? 
                (overallTotalCompliantReinspections / overallTotalReinspections) * 100 : 0;
              
              // Calculate leading cause using raw data
              const ruleColumn = allViolationsData[0]?.['Violated Rule Description'] !== undefined ? 'Violated Rule Description' :
                               allViolationsData[0]?.violated_rule_description !== undefined ? 'violated_rule_description' : null;
              
              let overallLeadingCause = 'Unknown';
              let overallLeadingCausePercentage = 0;
              
              if (ruleColumn) {
                const ruleCounts = {};
                allViolationsData.forEach(record => {
                  const rule = record[ruleColumn];
                  if (rule && rule.toString().trim() !== '') {
                    ruleCounts[rule] = (ruleCounts[rule] || 0) + 1;
                  }
                });
                
                if (Object.keys(ruleCounts).length > 0) {
                  const sortedRules = Object.entries(ruleCounts).sort((a, b) => b[1] - a[1]);
                  overallLeadingCause = sortedRules[0][0];
                  overallLeadingCausePercentage = (sortedRules[0][1] / allViolationsData.length) * 100;
                }
              }
              
              // Calculate overall severance data using raw data (matching SeverancesTab logic)
              const allSeverancesData = searchResults.data.severances || [];
              
              // Calculate resolved violations based on "Violation Resolved Date"
              const resolvedDateColumn = allSeverancesData[0]?.['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                                       allSeverancesData[0]?.violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
              
              let overallTotalResolvedSeverances = 0;
              let overallResolvedRecords = [];
              if (resolvedDateColumn) {
                overallResolvedRecords = allSeverancesData.filter(record => {
                  const resolvedDate = record[resolvedDateColumn];
                  return resolvedDate && resolvedDate.toString().trim() !== '';
                });
                overallTotalResolvedSeverances = overallResolvedRecords.length;
              }
              
              // Calculate average days to resolve (Certified Letter Date to Violation Resolved Date)
              const dateColumn = allSeverancesData[0]?.['Certified Letter Date'] !== undefined ? 'Certified Letter Date' :
                               allSeverancesData[0]?.certified_letter_date !== undefined ? 'certified_letter_date' : null;
              
              let overallAvgDaysToResolve = 0;
              if (overallResolvedRecords.length > 0 && dateColumn) {
                const validDays = overallResolvedRecords
                  .map(record => {
                    const certifiedDate = new Date(record[dateColumn]);
                    const resolvedDate = new Date(record[resolvedDateColumn]);
                    
                    if (!isNaN(certifiedDate.getTime()) && !isNaN(resolvedDate.getTime())) {
                      const timeDiff = resolvedDate.getTime() - certifiedDate.getTime();
                      return timeDiff / (1000 * 60 * 60 * 24); // Convert to days
                    }
                    return null;
                  })
                  .filter(days => days !== null);
                
                if (validDays.length > 0) {
                  overallAvgDaysToResolve = validDays.reduce((sum, days) => sum + days, 0) / validDays.length;
                }
              }
              
              // Calculate leading cause of certified letter issuance (Issue Reason)
              const issueReasonColumn = allSeverancesData[0]?.['Issue Reason'] !== undefined ? 'Issue Reason' :
                                      allSeverancesData[0]?.issue_reason !== undefined ? 'issue_reason' : null;
              
              let overallSeverancesLeadingCause = 'Unknown';
              let overallSeverancesLeadingCausePercentage = 0;
              
              if (issueReasonColumn) {
                const reasonCounts = {};
                allSeverancesData.forEach(record => {
                  const reason = record[issueReasonColumn];
                  if (reason && reason.toString().trim() !== '') {
                    const reasonKey = reason.toString().trim();
                    reasonCounts[reasonKey] = (reasonCounts[reasonKey] || 0) + 1;
                  }
                });
                
                // Find the most common reason
                const entries = Object.entries(reasonCounts);
                if (entries.length > 0) {
                  const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
                  overallSeverancesLeadingCause = sortedEntries[0][0];
                  overallSeverancesLeadingCausePercentage = ((sortedEntries[0][1] / allSeverancesData.length) * 100).toFixed(1);
                }
              }
              
              // Calculate overall spill data using raw data (matching SpillsTab logic)
              const allSpillsData = searchResults.data.spills || [];
              
              // Calculate totals from raw spill records (matching SpillsTab logic)
              const overallTotalSpillVolume = allSpillsData.reduce((sum, record) => {
                const volume = parseFloat(record['Gross Loss'] || record.gross_loss || 0);
                return sum + volume;
              }, 0);
              
              const overallTotalRecoveredVolume = allSpillsData.reduce((sum, record) => {
                const recovered = parseFloat(record['Recovered'] || record.recovered || 0);
                return sum + recovered;
              }, 0);
              
              const overallTotalNetLossVolume = allSpillsData.reduce((sum, record) => {
                const netLoss = parseFloat(record['Net Loss'] || record.net_loss || 0);
                return sum + netLoss;
              }, 0);
              
              // Calculate averages
              const overallAvgSpillVolume = allSpillsData.length > 0 ? overallTotalSpillVolume / allSpillsData.length : 0;
              const overallAvgRecoveryRate = overallTotalSpillVolume > 0 ? (overallTotalRecoveredVolume / overallTotalSpillVolume) * 100 : 0;
              const overallAvgLossRate = overallTotalSpillVolume > 0 ? (overallTotalNetLossVolume / overallTotalSpillVolume) * 100 : 0;
              
              // Calculate water impact count
              const waterImpactColumn = allSpillsData[0]?.['Water Impact'] !== undefined ? 'Water Impact' :
                                      allSpillsData[0]?.water_impact !== undefined ? 'water_impact' : null;
              
              let overallTotalWaterImpactCount = 0;
              if (waterImpactColumn) {
                overallTotalWaterImpactCount = allSpillsData.filter(record => {
                  const impact = record[waterImpactColumn];
                  return impact && impact.toString().toUpperCase() === 'YES';
                }).length;
              }
              
              // Calculate leading cause using raw data (matching SpillsTab logic)
              const causeColumn = allSpillsData[0]?.['Cause of Loss'] !== undefined ? 'Cause of Loss' :
                                 allSpillsData[0]?.cause_of_loss !== undefined ? 'cause_of_loss' : null;
              
              let overallSpillsLeadingCause = 'Unknown';
              let overallSpillsLeadingCausePercentage = 0;
              if (causeColumn) {
                const causeCounts = {};
                allSpillsData.forEach(record => {
                  const cause = record[causeColumn];
                  if (cause && cause.toString().trim() !== '') {
                    const causeStr = cause.toString().trim();
                    causeCounts[causeStr] = (causeCounts[causeStr] || 0) + 1;
                  }
                });
                
                const sortedCauses = Object.entries(causeCounts)
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                
                if (sortedCauses.length > 0) {
                  overallSpillsLeadingCause = sortedCauses[0][0];
                  overallSpillsLeadingCausePercentage = ((sortedCauses[0][1] / allSpillsData.length) * 100).toFixed(1);
                }
              }
              
              // Calculate top facility using raw data (matching SpillsTab logic)
              const facilityColumn = allSpillsData[0]?.['Facility Type'] !== undefined ? 'Facility Type' :
                                    allSpillsData[0]?.facility_type !== undefined ? 'facility_type' : null;
              
              let overallTopFacility = 'Unknown';
              let overallTopFacilityPercentage = 0;
              if (facilityColumn) {
                const facilityCounts = {};
                allSpillsData.forEach(record => {
                  const facility = record[facilityColumn];
                  if (facility && facility.toString().trim() !== '') {
                    const facilityStr = facility.toString().trim();
                    facilityCounts[facilityStr] = (facilityCounts[facilityStr] || 0) + 1;
                  }
                });
                
                const sortedFacilities = Object.entries(facilityCounts)
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                
                if (sortedFacilities.length > 0) {
                  overallTopFacility = sortedFacilities[0][0];
                  overallTopFacilityPercentage = ((sortedFacilities[0][1] / allSpillsData.length) * 100).toFixed(1);
                }
              }
              
              // Calculate top liquid type using raw data (matching SpillsTab logic)
              const liquidTypeColumn = allSpillsData[0]?.['Liquid Type'] !== undefined ? 'Liquid Type' :
                                     allSpillsData[0]?.liquid_type !== undefined ? 'liquid_type' : null;
              
              let overallTopLiquidType = 'Unknown';
              let overallTopLiquidTypePercentage = 0;
              if (liquidTypeColumn) {
                const liquidTypeCounts = {};
                allSpillsData.forEach(record => {
                  const liquidType = record[liquidTypeColumn];
                  if (liquidType && liquidType.toString().trim() !== '') {
                    const liquidTypeStr = liquidType.toString().trim();
                    liquidTypeCounts[liquidTypeStr] = (liquidTypeCounts[liquidTypeStr] || 0) + 1;
                  }
                });
                
                const sortedLiquidTypes = Object.entries(liquidTypeCounts)
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                
                if (sortedLiquidTypes.length > 0) {
                  overallTopLiquidType = sortedLiquidTypes[0][0];
                  overallTopLiquidTypePercentage = ((sortedLiquidTypes[0][1] / allSpillsData.length) * 100).toFixed(1);
                }
              }
              
              return (
                <Box sx={{ mb: 2 }}>
                  {/* Overall Header */}
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    {(() => {
                      // Calculate total red flags across all leases (use countyFlagsMap which has correct sums per county)
                      // This avoids double-counting from backward compatibility entries in redFlagsCache
                      let overallTotalRedFlags = 0;
                      if (countyFlagsMap) {
                        countyFlagsMap.forEach((totalFlags) => {
                          overallTotalRedFlags += totalFlags;
                        });
                      }
                      
                      return (
                        <>
                          {totalCounties.toLocaleString()} Count{totalCounties === 1 ? 'y' : 'ies'} | {totalLeases.toLocaleString()} Lease{totalLeases === 1 ? '' : 's'} | {totalWells.toLocaleString()} Well Record{totalWells === 1 ? '' : 's'} | {totalUniqueAPIs.toLocaleString()} Unique API{totalUniqueAPIs === 1 ? '' : 's'}{overallTotalRedFlags > 0 ? ` | ${overallTotalRedFlags.toLocaleString()} Flag${overallTotalRedFlags === 1 ? '' : 's'}` : ''}
                        </>
                      );
                    })()}
                  </Typography>
                  
                  {/* Overall Flow Diagram */}
                  {viewMode === 'summary' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {(() => {
                      const visibleCards = getVisibleCards();
                      return visibleCards.map((cardType, index) => {
                        const isLastCard = index === visibleCards.length - 1;
                        
                        return (
                          <React.Fragment key={cardType}>
                            {/* Wells Box */}
                            {cardType === 'wells' && (
                              <Paper sx={cardPaperSx}>
                                <Box sx={cardHeaderSx}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#8d6e63' }}>
                                    Wells
                                  </Typography>
                                </Box>
                                <Typography variant="caption" display="block">
                                  Leases: {totalLeases.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Well Records: {totalWells.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Unique APIs: {totalUniqueAPIs.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Est ARO: {totalARO > 0 ? `$${totalARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0'}
                                </Typography>
                                <Divider sx={dividerSx} />
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                    Inactive Total: {allInactiveWells.length.toLocaleString()}{allInactiveWells.length > 0 ? ` (${avgInactiveYears.toFixed(1)} years avg${formatARO(inactiveTotalARO) ? ` / ${formatARO(inactiveTotalARO)}` : ''})` : ''}
                                  </Typography>
                                  <FlagIconComponent count={summaryFlagCounts.wellsInactive} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                    Inactive &gt;5 Years: {inactiveOver5Years.length.toLocaleString()}{inactiveOver5Years.length > 0 ? ` (${avgInactiveOver5Years.toFixed(1)} years avg${formatARO(inactiveOver5YearsARO) ? ` / ${formatARO(inactiveOver5YearsARO)}` : ''})` : ''}
                                  </Typography>
                                  <FlagIconComponent count={summaryFlagCounts.wellsInactiveOver5} />
                                </Box>
                              </Paper>
                            )}

                            {/* Production Box */}
                            {cardType === 'production' && (
                              <Paper sx={cardPaperSx}>
                                <Box sx={cardHeaderSx}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                    Production (Avg)
                                  </Typography>
                                </Box>
                                {totalProduction > 0 ? (
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Oil: {overallAvgOilBblPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })} bbl/d
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Gas: {overallAvgGasMcfPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Flaring: {(overallAvgFlaringIntensity * overallAvgGasMcfPerDay / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Fuel Gas Use: {(overallAvgFuelGasIntensity * overallAvgGasMcfPerDay / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d
                                    </Typography>
                                    <Divider sx={dividerSx} />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Flaring Intensity: {overallAvgFlaringIntensity.toFixed(2)}%
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.productionFlaring} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Fuel Gas Use Intensity: {overallAvgFuelGasIntensity.toFixed(2)}%
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.productionFuelGas} />
                                    </Box>
                                  </Box>
                                ) : (
                                  <EmptyState message="No production data" />
                                )}
                              </Paper>
                            )}

                            {/* Inspections Box */}
                            {cardType === 'inspections' && (
                              <Paper sx={cardPaperSx}>
                                <Box sx={cardHeaderSx}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                    Inspections
                                  </Typography>
                                </Box>
                                {totalInspections > 0 ? (
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Count: {totalInspections.toLocaleString()}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Compliance: {overallAvgComplianceRate.toFixed(1)}%
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.inspectionsCompliance} />
                                    </Box>
                                  </Box>
                                ) : (
                                  <EmptyState message="No inspections data" />
                                )}
                              </Paper>
                            )}

                            {/* Violations Box */}
                            {cardType === 'violations' && (
                              <Paper sx={cardPaperSx}>
                                <Box sx={cardHeaderSx}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    Violations
                                  </Typography>
                                </Box>
                                {totalViolations > 0 ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Count: {totalViolations.toLocaleString()}
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.violationsCount} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Major: {overallTotalMajorViolations.toLocaleString()}
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.violationsMajor} />
                                    </Box>
                                    <Divider sx={dividerSx} />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Reinspection Compliance: {overallTotalReinspections === 0 ? 'N/A' : `${overallAvgReinspectionComplianceRate.toFixed(1)}%`}
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.violationsReinspection} />
                                    </Box>
                                    <Typography variant="caption" display="block">
                                      Not Compliant/Not Reinspected: {overallNotCompliantOrNotReinspected.toLocaleString()}
                                    </Typography>
                                    <Divider sx={dividerSx} />
                                    <Typography variant="caption" display="block">
                                      Leading Cause: {overallLeadingCause}{overallLeadingCausePercentage ? ` (${overallLeadingCausePercentage.toFixed(1)}%)` : ''}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <EmptyState message="No violations data" />
                                )}
                              </Paper>
                            )}

                            {/* Severances Box */}
                            {cardType === 'severances' && (
                              <Paper sx={cardPaperSx}>
                                <Box sx={cardHeaderSx}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#607d8b' }}>
                                    Severances
                                  </Typography>
                                </Box>
                                {totalSeverances > 0 ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Count: {totalSeverances.toLocaleString()}
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.severancesCount} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                      Resolved: {totalSeverances > 0 ? ((overallTotalResolvedSeverances / totalSeverances) * 100).toFixed(1) : 0}%
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.severancesResolved} />
                                    </Box>
                                    <Typography variant="caption" display="block">
                                      Avg Days to Resolve: {Math.round(overallAvgDaysToResolve)}
                                    </Typography>
                                    <Divider sx={dividerSx} />
                                    <Typography variant="caption" display="block">
                                      Leading Cause: {overallSeverancesLeadingCause}{overallSeverancesLeadingCausePercentage ? ` (${overallSeverancesLeadingCausePercentage}%)` : ''}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <EmptyState message="No severances data" />
                                )}
                              </Paper>
                            )}

                            {/* Spills Box */}
                            {cardType === 'spills' && (
                              <Paper sx={cardPaperSx}>
                                <Box sx={cardHeaderSx}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.dark' }}>
                                    Spills
                                  </Typography>
                                </Box>
                                {totalSpills > 0 ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary', flex: 1 }}>
                                      Count: {totalSpills.toLocaleString()}
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.spillsCount} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary', flex: 1 }}>
                                      Water Impact Count: {overallTotalWaterImpactCount.toLocaleString()}
                                    </Typography>
                                      <FlagIconComponent count={summaryFlagCounts.spillsWaterImpact} />
                                    </Box>
                                    <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                      Avg Spill: {Math.round(overallAvgSpillVolume).toLocaleString()} bbls
                                    </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Total Reported Spilled: {Math.round(overallTotalSpillVolume).toLocaleString()} bbls
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Volume Recovered: {overallAvgRecoveryRate.toFixed(1)}%
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Volume Lost: {overallAvgLossRate.toFixed(1)}%
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Leading Cause: {overallSpillsLeadingCause}{overallSpillsLeadingCausePercentage ? ` (${overallSpillsLeadingCausePercentage}%)` : ''}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Top Occurrence Facility: {overallTopFacility}{overallTopFacilityPercentage ? ` (${overallTopFacilityPercentage}%)` : ''}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Highest Liquid Spilled: {overallTopLiquidType}{overallTopLiquidTypePercentage ? ` (${overallTopLiquidTypePercentage}%)` : ''}
                                      </Typography>
                                  </Box>
                                ) : (
                                  <EmptyState message="No spills data" />
                                )}
                              </Paper>
                            )}

                            {/* Arrow - only show if not the last card */}
                            {!isLastCard && (
                              <Typography variant="h4" sx={{ color: 'text.secondary' }}>→</Typography>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </Box>
                  )}
                </Box>
              );
            })()}
          </Box>

        {/* Sorting Controls */}
        {(viewMode === 'county' || viewMode === 'lease') && (
          <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sort Counties</InputLabel>
              <Select
                value={countySortBy}
                label="Sort Counties"
                onChange={(e) => setCountySortBy(e.target.value)}
              >
                <MenuItem value="A-Z">A-Z</MenuItem>
                <MenuItem value="Z-A">Z-A</MenuItem>
                <MenuItem value="Leases High to Low">Leases High to Low</MenuItem>
                <MenuItem value="Leases Low to High">Leases Low to High</MenuItem>
                <MenuItem value="Wells High to Low">Wells High to Low</MenuItem>
                <MenuItem value="Wells Low to High">Wells Low to High</MenuItem>
                <MenuItem value="Flags High to Low">Flags High to Low</MenuItem>
                <MenuItem value="Flags Low to High">Flags Low to High</MenuItem>
              </Select>
            </FormControl>
            
            {viewMode === 'lease' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Sort Leases</InputLabel>
                <Select
                  value={leaseSortBy}
                  label="Sort Leases"
                  onChange={(e) => setLeaseSortBy(e.target.value)}
                >
                  <MenuItem value="A-Z">A-Z</MenuItem>
                  <MenuItem value="Z-A">Z-A</MenuItem>
                  <MenuItem value="Wells High to Low">Wells High to Low</MenuItem>
                  <MenuItem value="Wells Low to High">Wells Low to High</MenuItem>
                  <MenuItem value="Flags High to Low">Flags High to Low</MenuItem>
                  <MenuItem value="Flags Low to High">Flags Low to High</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* County Sections - Only show in County or Lease view */}
        {(viewMode === 'county' || viewMode === 'lease') && (() => {
          // Use memoized sorted counties for performance
          const sortedCounties = sortedCountiesMemo;

          return sortedCounties.map(([county, leases]) => (
          <Box key={county} sx={{ mb: 0 }}>
            {/* County Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              py: 0.5,
              px: 1,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              color: 'text.primary',
              cursor: 'pointer',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.24)',
              border: '1px solid #e0e0e0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
            onClick={() => toggleCountyExpansion(county)}
            >
              <Typography variant="body2" sx={{ fontWeight: 'normal', flexGrow: 1 }}>
                    {(() => {
                      // Calculate total red flags for this county (use countyFlagsMap which has correct county-specific totals)
                      // This ensures accuracy for leases that span multiple counties
                      const countyTotalRedFlags = countyFlagsMap.get(county.toUpperCase()) || 0;
                      
                      // Calculate unique APIs for this county
                      const countyWellsData = (searchResults?.data?.wells || []).filter(well => {
                        const wellCounty = well['County'] || well.county || 'Unknown County';
                        return wellCounty === county;
                      });
                      const countyUniqueAPIs = new Set();
                      countyWellsData.forEach(well => {
                        const apiNumber = well['API Number'] || well.api_number || well.api || well.apiNumber;
                        if (apiNumber && apiNumber !== 'Unknown API' && apiNumber !== '') {
                          countyUniqueAPIs.add(String(apiNumber).trim());
                        }
                      });
                      const countyTotalUniqueAPIs = countyUniqueAPIs.size;
                      
                      const wellCount = countyCounts.get(county)?.wellCount || Array.from(leases.values()).reduce((total, lease) => total + lease.wells.length, 0);
                  
                  // Format county name: capitalize first letter, lowercase rest, add "County"
                  const formatCountyName = (countyName) => {
                    if (!countyName) return 'Unknown County';
                    // Remove "COUNTY" if already present (case-insensitive)
                    const cleaned = countyName.replace(/\s+county$/i, '');
                    // Capitalize first letter, lowercase rest
                    const formatted = cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
                    return `${formatted} County`;
                  };
                  
                  return (
                    <>
                      <Box component="span" sx={{ fontWeight: 'bold', fontSize: '0.95rem', mr: 1 }}>
                        {formatCountyName(county)}
                      </Box>
                      <Box component="span" sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
                        — {countyCounts.get(county)?.leaseCount?.toLocaleString() || leases.size.toLocaleString()} Lease{(countyCounts.get(county)?.leaseCount || leases.size) === 1 ? '' : 's'} | {wellCount.toLocaleString()} Well Record{wellCount === 1 ? '' : 's'} | {countyTotalUniqueAPIs.toLocaleString()} Unique API{countyTotalUniqueAPIs === 1 ? '' : 's'}{countyTotalRedFlags > 0 ? ` | ${countyTotalRedFlags.toLocaleString()} Flag${countyTotalRedFlags === 1 ? '' : 's'}` : ''}
                      </Box>
                    </>
                  );
                })()}
              </Typography>
              <IconButton 
                size="small" 
                sx={{ 
                  color: 'inherit', 
                  ml: 1,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'scale(1.1)'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCountyExpansion(county);
                }}
              >
                {expandedCounties.has(county) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* County-Level Summary and Lease Chips */}
            <Collapse in={expandedCounties.has(county)}>
              {/* County-Level Flow Diagram */}
              {(() => {
                // Calculate county-level aggregated data using raw data
                const totalWells = Array.from(leases.values()).reduce((sum, lease) => sum + lease.wells.length, 0);
                
                // Use raw data for counts (more accurate than summing processed counts)
                const totalViolations = (searchResults.data.violations || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                }).length;
                
                const totalSpills = (searchResults.data.spills || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                }).length;
                
                const totalSeverances = (searchResults.data.severances || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                }).length;
                
                const totalInspections = (searchResults.data.inspections || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                }).length;
                
                // Calculate aggregated production data - use raw production data filtered by county
                const countyProductionData = (searchResults.data.production || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                });
                const totalCountyProduction = countyProductionData.length; // Record count for condition check
                
                // Calculate totals from raw production records (matching ProductionTab logic)
                const totalOilBbl = countyProductionData.reduce((sum, record) => {
                  const oil = parseFloat(record['Oil BBL'] || record.oil_bbl || 0);
                  return sum + oil;
                }, 0);
                
                const totalGasMcf = countyProductionData.reduce((sum, record) => {
                  const gas = parseFloat(record['Gas MCF'] || record.gas_mcf || 0);
                  return sum + gas;
                }, 0);
                
                const totalFlareGasMcf = countyProductionData.reduce((sum, record) => {
                  const flaring = parseFloat(record['Flare Gas MCF'] || record.flare_gas_mcf || 0);
                  return sum + flaring;
                }, 0);
                
                const totalFuelGasMcf = countyProductionData.reduce((sum, record) => {
                  const fuelGas = parseFloat(record['Fuel Gas MCF'] || record.fuel_gas_mcf || 0);
                  return sum + fuelGas;
                }, 0);
                
                // Calculate unique production months and total days (matching ProductionTab logic)
                const uniqueMonths = new Set(countyProductionData.map(record => record['Production Month'] || record.production_month));
                const totalDays = Array.from(uniqueMonths).reduce((sum, month) => {
                  // Find first record for this month to get its days_month
                  const firstRecord = countyProductionData.find(record => (record['Production Month'] || record.production_month) === month);
                  return sum + (parseFloat(firstRecord?.['Days in Month'] || firstRecord?.days_month) || 0);
                }, 0);
                
                // Calculate daily averages from aggregated totals
                const avgOilBblPerDay = totalDays > 0 ? totalOilBbl / totalDays : 0;
                const avgGasMcfPerDay = totalDays > 0 ? totalGasMcf / totalDays : 0;
                const avgFlaringIntensity = totalGasMcf > 0 ? (totalFlareGasMcf / totalGasMcf) * 100 : 0;
                const avgFuelGasIntensity = totalGasMcf > 0 ? (totalFuelGasMcf / totalGasMcf) * 100 : 0;
                
                // Calculate aggregated compliance rates using raw data (matching InspectionsTab logic)
                const countyInspectionsData = (searchResults.data.inspections || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                });
                
                const complianceColumn = countyInspectionsData[0]?.['Compliance'] !== undefined ? 'Compliance' :
                                      countyInspectionsData[0]?.compliance !== undefined ? 'compliance' : null;
                
                let avgComplianceRate = 0;
                if (complianceColumn) {
                  const compliantInspections = countyInspectionsData.filter(record => {
                    const compliance = record[complianceColumn];
                    return compliance && compliance.toString().toUpperCase() === 'YES';
                  }).length;
                  avgComplianceRate = countyInspectionsData.length > 0 ? (compliantInspections / countyInspectionsData.length) * 100 : 0;
                }
                
                // Calculate aggregated violation data using raw data (matching ViolationsTab logic)
                const countyViolationsData = (searchResults.data.violations || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                });
                
                // Calculate major violations count
                const majorViolationColumn = countyViolationsData[0]?.['Major Violation'] !== undefined ? 'Major Violation' :
                                          countyViolationsData[0]?.major_violation !== undefined ? 'major_violation' : null;
                
                let totalMajorViolations = 0;
                if (majorViolationColumn) {
                  totalMajorViolations = countyViolationsData.filter(record => {
                    const majorViolation = record[majorViolationColumn];
                    return majorViolation && majorViolation.toString().toUpperCase() === 'YES';
                  }).length;
                }
                
                // Calculate reinspection compliance using raw data (matching ViolationsTab logic)
                const reinspectionColumn = countyViolationsData[0]?.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
                
                let totalReinspections = 0;
                let totalCompliantReinspections = 0;
                let notCompliantOrNotReinspected = 0;
                
                if (reinspectionColumn) {
                  // Count reinspections: records where field is not empty and not "--"
                  const reinspectionRecords = countyViolationsData.filter(record => {
                    const reinspection = record[reinspectionColumn];
                    return reinspection && reinspection.toString().trim() !== '' && reinspection.toString().trim() !== '--';
                  });
                  
                  totalReinspections = reinspectionRecords.length;
                  
                  // Count compliant reinspections: records where field equals "Y"
                  totalCompliantReinspections = reinspectionRecords.filter(record => {
                    const compliance = record[reinspectionColumn];
                    return compliance && compliance.toString().toUpperCase() === 'Y';
                  }).length;
                  
                  // Not Compliant/Not Reinspected = Total violations - Total reinspections
                  notCompliantOrNotReinspected = countyViolationsData.length - totalReinspections;
                }
                
                const avgReinspectionComplianceRate = totalReinspections > 0 ? 
                  (totalCompliantReinspections / totalReinspections) * 100 : 0;
                
                // Calculate leading cause using raw data
                const ruleColumn = countyViolationsData[0]?.['Violated Rule Description'] !== undefined ? 'Violated Rule Description' :
                                 countyViolationsData[0]?.violated_rule_description !== undefined ? 'violated_rule_description' : null;
                
                let leadingCause = 'Unknown';
                let leadingCausePercentage = 0;
                
                if (ruleColumn) {
                  const ruleCounts = {};
                  countyViolationsData.forEach(record => {
                    const rule = record[ruleColumn];
                    if (rule && rule.toString().trim() !== '') {
                      ruleCounts[rule] = (ruleCounts[rule] || 0) + 1;
                    }
                  });
                  
                  if (Object.keys(ruleCounts).length > 0) {
                    const sortedRules = Object.entries(ruleCounts).sort((a, b) => b[1] - a[1]);
                    leadingCause = sortedRules[0][0];
                    leadingCausePercentage = (sortedRules[0][1] / countyViolationsData.length) * 100;
                  }
                }
                
                // Calculate aggregated severance data using raw data (matching SeverancesTab logic)
                const countySeverancesData = (searchResults.data.severances || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                });
                
                // Calculate resolved violations based on "Violation Resolved Date"
                const resolvedDateColumn = countySeverancesData[0]?.['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                                         countySeverancesData[0]?.violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
                
                let totalResolvedSeverances = 0;
                let resolvedRecords = [];
                if (resolvedDateColumn) {
                  resolvedRecords = countySeverancesData.filter(record => {
                    const resolvedDate = record[resolvedDateColumn];
                    return resolvedDate && resolvedDate.toString().trim() !== '';
                  });
                  totalResolvedSeverances = resolvedRecords.length;
                }
                
                // Calculate average days to resolve (Certified Letter Date to Violation Resolved Date)
                const dateColumn = countySeverancesData[0]?.['Certified Letter Date'] !== undefined ? 'Certified Letter Date' :
                                 countySeverancesData[0]?.certified_letter_date !== undefined ? 'certified_letter_date' : null;
                
                let avgDaysToResolve = 0;
                if (resolvedRecords.length > 0 && dateColumn) {
                  const validDays = resolvedRecords
                    .map(record => {
                      const certifiedDate = new Date(record[dateColumn]);
                      const resolvedDate = new Date(record[resolvedDateColumn]);
                      
                      if (!isNaN(certifiedDate.getTime()) && !isNaN(resolvedDate.getTime())) {
                        const timeDiff = resolvedDate.getTime() - certifiedDate.getTime();
                        return timeDiff / (1000 * 60 * 60 * 24); // Convert to days
                      }
                      return null;
                    })
                    .filter(days => days !== null);
                  
                  if (validDays.length > 0) {
                    avgDaysToResolve = validDays.reduce((sum, days) => sum + days, 0) / validDays.length;
                  }
                }
                
                // Calculate leading cause of certified letter issuance (Issue Reason)
                const issueReasonColumn = countySeverancesData[0]?.['Issue Reason'] !== undefined ? 'Issue Reason' :
                                        countySeverancesData[0]?.issue_reason !== undefined ? 'issue_reason' : null;
                
                let countySeverancesLeadingCause = 'Unknown';
                let countySeverancesLeadingCausePercentage = 0;
                
                if (issueReasonColumn) {
                  const reasonCounts = {};
                  countySeverancesData.forEach(record => {
                    const reason = record[issueReasonColumn];
                    if (reason && reason.toString().trim() !== '') {
                      const reasonKey = reason.toString().trim();
                      reasonCounts[reasonKey] = (reasonCounts[reasonKey] || 0) + 1;
                    }
                  });
                  
                  // Find the most common reason
                  const entries = Object.entries(reasonCounts);
                  if (entries.length > 0) {
                    const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
                    countySeverancesLeadingCause = sortedEntries[0][0];
                    countySeverancesLeadingCausePercentage = ((sortedEntries[0][1] / countySeverancesData.length) * 100).toFixed(1);
                  }
                }
                
                // Calculate aggregated spill data using raw data (matching SpillsTab logic)
                const countySpillsData = (searchResults.data.spills || []).filter(record => {
                  const recordCounty = record['County'] || record.county || 'Unknown County';
                  return recordCounty === county;
                });
                
                // Calculate totals from raw spill records (matching SpillsTab logic)
                const totalSpillVolume = countySpillsData.reduce((sum, record) => {
                  const volume = parseFloat(record['Gross Loss'] || record.gross_loss || 0);
                  return sum + volume;
                }, 0);
                
                const totalRecoveredVolume = countySpillsData.reduce((sum, record) => {
                  const recovered = parseFloat(record['Recovered'] || record.recovered || 0);
                  return sum + recovered;
                }, 0);
                
                const totalNetLossVolume = countySpillsData.reduce((sum, record) => {
                  const netLoss = parseFloat(record['Net Loss'] || record.net_loss || 0);
                  return sum + netLoss;
                }, 0);
                
                // Calculate averages
                const avgSpillVolume = countySpillsData.length > 0 ? totalSpillVolume / countySpillsData.length : 0;
                const avgRecoveryRate = totalSpillVolume > 0 ? (totalRecoveredVolume / totalSpillVolume) * 100 : 0;
                const avgLossRate = totalSpillVolume > 0 ? (totalNetLossVolume / totalSpillVolume) * 100 : 0;
                
                // Calculate water impact count
                const waterImpactColumn = countySpillsData[0]?.['Water Impact'] !== undefined ? 'Water Impact' :
                                        countySpillsData[0]?.water_impact !== undefined ? 'water_impact' : null;
                
                let totalWaterImpactCount = 0;
                if (waterImpactColumn) {
                  totalWaterImpactCount = countySpillsData.filter(record => {
                    const impact = record[waterImpactColumn];
                    return impact && impact.toString().toUpperCase() === 'YES';
                  }).length;
                }
                
                // Calculate leading cause using raw data (matching SpillsTab logic)
                const causeColumn = countySpillsData[0]?.['Cause of Loss'] !== undefined ? 'Cause of Loss' :
                                   countySpillsData[0]?.cause_of_loss !== undefined ? 'cause_of_loss' : null;
                
                let countySpillsLeadingCause = 'Unknown';
                let countySpillsLeadingCausePercentage = 0;
                if (causeColumn) {
                  const causeCounts = {};
                  countySpillsData.forEach(record => {
                    const cause = record[causeColumn];
                    if (cause && cause.toString().trim() !== '') {
                      const causeStr = cause.toString().trim();
                      causeCounts[causeStr] = (causeCounts[causeStr] || 0) + 1;
                    }
                  });
                  
                  const sortedCauses = Object.entries(causeCounts)
                    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                  
                  if (sortedCauses.length > 0) {
                    countySpillsLeadingCause = sortedCauses[0][0];
                    countySpillsLeadingCausePercentage = ((sortedCauses[0][1] / countySpillsData.length) * 100).toFixed(1);
                  }
                }
                
                // Calculate top facility using raw data (matching SpillsTab logic)
                const facilityColumn = countySpillsData[0]?.['Facility Type'] !== undefined ? 'Facility Type' :
                                      countySpillsData[0]?.facility_type !== undefined ? 'facility_type' : null;
                
                let topFacility = 'Unknown';
                let topFacilityPercentage = 0;
                if (facilityColumn) {
                  const facilityCounts = {};
                  countySpillsData.forEach(record => {
                    const facility = record[facilityColumn];
                    if (facility && facility.toString().trim() !== '') {
                      const facilityStr = facility.toString().trim();
                      facilityCounts[facilityStr] = (facilityCounts[facilityStr] || 0) + 1;
                    }
                  });
                  
                  const sortedFacilities = Object.entries(facilityCounts)
                    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                  
                  if (sortedFacilities.length > 0) {
                    topFacility = sortedFacilities[0][0];
                    topFacilityPercentage = ((sortedFacilities[0][1] / countySpillsData.length) * 100).toFixed(1);
                  }
                }
                
                // Calculate top liquid type using raw data (matching SpillsTab logic)
                const liquidTypeColumn = countySpillsData[0]?.['Liquid Type'] !== undefined ? 'Liquid Type' :
                                       countySpillsData[0]?.liquid_type !== undefined ? 'liquid_type' : null;
                
                let topLiquidType = 'Unknown';
                let topLiquidTypePercentage = 0;
                if (liquidTypeColumn) {
                  const liquidTypeCounts = {};
                  countySpillsData.forEach(record => {
                    const liquidType = record[liquidTypeColumn];
                    if (liquidType && liquidType.toString().trim() !== '') {
                      const liquidTypeStr = liquidType.toString().trim();
                      liquidTypeCounts[liquidTypeStr] = (liquidTypeCounts[liquidTypeStr] || 0) + 1;
                    }
                  });
                  
                  const sortedLiquidTypes = Object.entries(liquidTypeCounts)
                    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                  
                  if (sortedLiquidTypes.length > 0) {
                    topLiquidType = sortedLiquidTypes[0][0];
                    topLiquidTypePercentage = ((sortedLiquidTypes[0][1] / countySpillsData.length) * 100).toFixed(1);
                  }
                }
                
                // Calculate flag counts per specific metric for this county
                const countyFlagCounts = (() => {
                  let wellsInactive = 0;
                  let wellsInactiveOver5 = 0;
                  let productionFlaring = 0;
                  let productionFuelGas = 0;
                  let inspectionsCompliance = 0;
                  let violationsCount = 0;
                  let violationsMajor = 0;
                  let violationsReinspection = 0;
                  let severancesCount = 0;
                  let severancesResolved = 0;
                  let spillsCount = 0;
                  let spillsWaterImpact = 0;

                  leases.forEach((leaseData, leaseKey) => {
                    // Use county-lease key for accurate flag counts
                    const countyLeaseKey = `${county}-${leaseKey}`;
                    const cached = redFlagsCache.get(countyLeaseKey);
                    if (cached) {
                      const { flags } = cached;
                      
                      // Wells flags: count leases with inactive wells
                      if (flags.inactiveWells) {
                        if (leaseData.wells && leaseData.wells.length > 0) {
                          const inactiveWells = leaseData.wells.filter(well => isInactiveWell(well));
                          wellsInactive += inactiveWells.length;
                        }
                      }
                      
                      if (flags.inactiveWellsOver5Years) {
                        if (leaseData.wells && leaseData.wells.length > 0) {
                          const inactiveWells = leaseData.wells.filter(well => isInactiveWell(well));
                          const inactiveOver5Years = inactiveWells.filter(well => {
                            const inactiveYears = well.inactiveYears !== undefined ? well.inactiveYears : (well.inactive_years || 0);
                            return inactiveYears > 5;
                          });
                          wellsInactiveOver5 += inactiveOver5Years.length;
                        }
                      }
                      
                      // Production flags: count leases with flags
                      if (flags.flaringIntensity) productionFlaring += 1;
                      if (flags.fuelGasIntensity) productionFuelGas += 1;
                      
                      // Inspections flags
                      if (flags.inspectionsCompliance) inspectionsCompliance += 1;
                      
                      // Violations flags
                      if (flags.violationsCount) violationsCount += 1;
                      if (flags.violationsMajor) violationsMajor += 1;
                      if (flags.violationsReinspection) violationsReinspection += 1;
                      
                      // Severances flags
                      if (flags.severancesCount) severancesCount += 1;
                      if (flags.severancesResolved) severancesResolved += 1;
                      
                      // Spills flags
                      if (flags.spillsCount) spillsCount += 1;
                      if (flags.spillsWaterImpact) spillsWaterImpact += 1;
                    }
                  });

                  return {
                    wellsInactive,
                    wellsInactiveOver5,
                    productionFlaring,
                    productionFuelGas,
                    inspectionsCompliance,
                    violationsCount,
                    violationsMajor,
                    violationsReinspection,
                    severancesCount,
                    severancesResolved,
                    spillsCount,
                    spillsWaterImpact
                  };
                })();
                
                return (
                  <Box sx={{ mb: viewMode === 'county' ? 3 : 0 }}>
                    {/* County-Level Flow Diagram - Only show in County view */}
                    {viewMode === 'county' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {(() => {
                        const visibleCards = getVisibleCards();
                        return visibleCards.map((cardType, index) => {
                          const isLastCard = index === visibleCards.length - 1;
                          
                          return (
                            <React.Fragment key={cardType}>
                              {/* Wells Box */}
                              {cardType === 'wells' && (
                                <Paper sx={{ 
                                  p: 1, 
                                  minWidth: 160,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1
                                }}>
                                  {(() => {
                                    // Calculate inactive wells statistics for this county
                                    const countyWellsData = (searchResults.data.wells || []).filter(well => {
                                      const wellCounty = well['County'] || well.county || 'Unknown County';
                                      return wellCounty === county;
                                    });
                                    
                                    // Calculate unique APIs for this county
                                    const countyUniqueAPIs = new Set();
                                    countyWellsData.forEach(well => {
                                      const apiNumber = well['API Number'] || well.api_number || well.api || well.apiNumber;
                                      if (apiNumber && apiNumber !== 'Unknown API' && apiNumber !== '') {
                                        countyUniqueAPIs.add(String(apiNumber).trim());
                                      }
                                    });
                                    const countyTotalUniqueAPIs = countyUniqueAPIs.size;
                                    
                                    const countyInactiveWells = countyWellsData.filter(well => isInactiveWell(well));
                                    const countyInactiveOver5Years = countyInactiveWells.filter(well => {
                                      const inactiveYears = well.inactive_years || 0;
                                      return inactiveYears > 5;
                                    });
                                    
                                    // Calculate average inactive years
                                    const countyAvgInactiveYears = countyInactiveWells.length > 0
                                      ? countyInactiveWells.reduce((sum, well) => sum + (well.inactive_years || 0), 0) / countyInactiveWells.length
                                      : 0;
                                    
                                    const countyAvgInactiveOver5Years = countyInactiveOver5Years.length > 0
                                      ? countyInactiveOver5Years.reduce((sum, well) => sum + (well.inactive_years || 0), 0) / countyInactiveOver5Years.length
                                      : 0;
                                    
                                    // Helper function to get plugging cost (ARO) for a well
                                    const getCountyPluggingCost = (well) => {
                                      const pluggingCost = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
                                      if (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
                                          typeof pluggingCost === 'number' && !isNaN(pluggingCost)) {
                                        return pluggingCost;
                                      }
                                      return 0;
                                    };
                                    
                                    // Calculate ARO for county inactive wells
                                    const countyInactiveTotalARO = countyInactiveWells.reduce((sum, well) => sum + getCountyPluggingCost(well), 0);
                                    const countyInactiveOver5YearsARO = countyInactiveOver5Years.reduce((sum, well) => sum + getCountyPluggingCost(well), 0);
                                    
                                    // Calculate total ARO for all county wells
                                    const countyTotalARO = countyWellsData.reduce((sum, well) => sum + getCountyPluggingCost(well), 0);
                                    
                                    // Format ARO in regular dollar format (for inactive lines and Est ARO)
                                    const formatCountyARO = (aro) => {
                                      if (aro === 0) return null;
                                      return `ARO $${aro.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                                    };
                                    
                                    return (
                                      <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#8d6e63' }}>
                                            Wells
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" display="block">
                                    Leases: {leases.size.toLocaleString()}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                          Well Records: {totalWells.toLocaleString()}
                                  </Typography>
                                        <Typography variant="caption" display="block">
                                          Unique APIs: {countyTotalUniqueAPIs.toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Est ARO: {countyTotalARO > 0 ? `$${countyTotalARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0'}
                                        </Typography>
                                        <Divider sx={dividerSx} />
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                            Inactive Total: {countyInactiveWells.length.toLocaleString()}{countyInactiveWells.length > 0 ? ` (${countyAvgInactiveYears.toFixed(1)} years avg${formatCountyARO(countyInactiveTotalARO) ? ` / ${formatCountyARO(countyInactiveTotalARO)}` : ''})` : ''}
                                          </Typography>
                                          <FlagIconComponent count={countyFlagCounts.wellsInactive} />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                            Inactive &gt;5 Years: {countyInactiveOver5Years.length.toLocaleString()}{countyInactiveOver5Years.length > 0 ? ` (${countyAvgInactiveOver5Years.toFixed(1)} years avg${formatCountyARO(countyInactiveOver5YearsARO) ? ` / ${formatCountyARO(countyInactiveOver5YearsARO)}` : ''})` : ''}
                                          </Typography>
                                          <FlagIconComponent count={countyFlagCounts.wellsInactiveOver5} />
                                        </Box>
                                      </>
                                    );
                                  })()}
                                </Paper>
                              )}

                              {/* Production Box */}
                              {cardType === 'production' && (
                                <Paper sx={{ 
                                  p: 1, 
                                  minWidth: 160,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                      Production (Avg)
                                    </Typography>
                                  </Box>
                                  {totalCountyProduction > 0 ? (
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        Oil: {avgOilBblPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })} bbl/d
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Gas: {avgGasMcfPerDay.toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Flaring: {(avgFlaringIntensity * avgGasMcfPerDay / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Fuel Gas Use: {(avgFuelGasIntensity * avgGasMcfPerDay / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })} Mcf/d
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Flaring Intensity: {avgFlaringIntensity.toFixed(2)}%
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.productionFlaring} />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Fuel Gas Use Intensity: {avgFuelGasIntensity.toFixed(2)}%
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.productionFuelGas} />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                                      <Typography variant="caption">No production data</Typography>
                                    </Box>
                                  )}
                                </Paper>
                              )}

                              {/* Inspections Box */}
                              {cardType === 'inspections' && (
                                <Paper sx={{ 
                                  p: 1, 
                                  minWidth: 160,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                      Inspections
                                    </Typography>
                                  </Box>
                                  {totalInspections > 0 ? (
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        Count: {totalInspections.toLocaleString()}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Compliance: {avgComplianceRate.toFixed(1)}%
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.inspectionsCompliance} />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                                      <Typography variant="caption">No inspections data</Typography>
                                    </Box>
                                  )}
                                </Paper>
                              )}

                              {/* Violations Box */}
                              {cardType === 'violations' && (
                                <Paper sx={{ 
                                  p: 1, 
                                  minWidth: 160,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                      Violations
                                    </Typography>
                                  </Box>
                                  {totalViolations > 0 ? (
                                    <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Count: {totalViolations.toLocaleString()}
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.violationsCount} />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Major: {totalMajorViolations.toLocaleString()}
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.violationsMajor} />
                                      </Box>
                                      <Divider sx={dividerSx} />
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Reinspection Compliance: {totalReinspections === 0 ? 'N/A' : `${avgReinspectionComplianceRate.toFixed(1)}%`}
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.violationsReinspection} />
                                      </Box>
                                      <Typography variant="caption" display="block">
                                        Not Compliant/Not Reinspected: {notCompliantOrNotReinspected.toLocaleString()}
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Typography variant="caption" display="block">
                                        Leading Cause: {leadingCause}{leadingCausePercentage ? ` (${leadingCausePercentage.toFixed(1)}%)` : ''}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                                      <Typography variant="caption">No violations data</Typography>
                                    </Box>
                                  )}
                                </Paper>
                              )}

                              {/* Severances Box */}
                              {cardType === 'severances' && (
                                <Paper sx={{ 
                                  p: 1, 
                                  minWidth: 160,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#607d8b' }}>
                                      Severances
                                    </Typography>
                                  </Box>
                                  {totalSeverances > 0 ? (
                                    <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Count: {totalSeverances.toLocaleString()}
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.severancesCount} />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ flex: 1 }}>
                                        Resolved: {totalSeverances > 0 ? ((totalResolvedSeverances / totalSeverances) * 100).toFixed(1) : 0}%
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.severancesResolved} />
                                      </Box>
                                      <Typography variant="caption" display="block">
                                        Avg Days to Resolve: {Math.round(avgDaysToResolve)}
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Typography variant="caption" display="block">
                                        Leading Cause: {countySeverancesLeadingCause}{countySeverancesLeadingCausePercentage ? ` (${countySeverancesLeadingCausePercentage}%)` : ''}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                                      <Typography variant="caption">No severances data</Typography>
                                    </Box>
                                  )}
                                </Paper>
                              )}

                              {/* Spills Box */}
                              {cardType === 'spills' && (
                                <Paper sx={{ 
                                  p: 1, 
                                  minWidth: 160,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.dark' }}>
                                      Spills
                                    </Typography>
                                  </Box>
                                  {totalSpills > 0 ? (
                                    <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ color: 'text.primary', flex: 1 }}>
                                        Count: {totalSpills.toLocaleString()}
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.spillsCount} />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="caption" display="block" sx={{ color: 'text.primary', flex: 1 }}>
                                        Water Impact Count: {totalWaterImpactCount.toLocaleString()}
                                      </Typography>
                                        <FlagIconComponent count={countyFlagCounts.spillsWaterImpact} />
                                      </Box>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Avg Spill: {Math.round(avgSpillVolume).toLocaleString()} bbls
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Total Reported Spilled: {Math.round(totalSpillVolume).toLocaleString()} bbls
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Volume Recovered: {avgRecoveryRate.toFixed(1)}%
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Volume Lost: {avgLossRate.toFixed(1)}%
                                      </Typography>
                                      <Divider sx={dividerSx} />
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Leading Cause: {countySpillsLeadingCause}{countySpillsLeadingCausePercentage ? ` (${countySpillsLeadingCausePercentage}%)` : ''}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Top Occurrence Facility: {topFacility}{topFacilityPercentage ? ` (${topFacilityPercentage}%)` : ''}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                                        Highest Liquid Spilled: {topLiquidType}{topLiquidTypePercentage ? ` (${topLiquidTypePercentage}%)` : ''}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                                      <Typography variant="caption">No spills data</Typography>
                                    </Box>
                                  )}
                                </Paper>
                              )}

                              {/* Arrow - only show if not the last card */}
                              {!isLastCard && (
                                <Typography variant="h4" sx={{ color: 'text.secondary' }}>→</Typography>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </Box>
                    )}
                  </Box>
                );
              })()}
              
              {/* Lease Chips - Only show in Lease view */}
              {viewMode === 'lease' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {(() => {
                  // Sort leases based on selected criteria
                  const sortedLeases = Array.from(leases.entries()).sort(([leaseNumberA, leaseDataA], [leaseNumberB, leaseDataB]) => {
                    let comparison = 0;
                    
                    // Helper to get first non-unknown lease name
                    const getFirstName = (leaseData) => {
                      if (Array.isArray(leaseData.leaseName)) {
                        const realNames = leaseData.leaseName.filter(name => name && name !== 'Unknown Lease');
                        return realNames.length > 0 ? realNames[0] : 'Unknown Lease';
                      }
                      return leaseData.leaseName;
                    };
                    
                    switch (leaseSortBy) {
                      case 'A-Z':
                        comparison = getFirstName(leaseDataA).localeCompare(getFirstName(leaseDataB));
                        break;
                      case 'Z-A':
                        comparison = getFirstName(leaseDataB).localeCompare(getFirstName(leaseDataA));
                        break;
                      case 'Wells High to Low':
                        comparison = leaseDataB.wells.length - leaseDataA.wells.length;
                        break;
                      case 'Wells Low to High':
                        comparison = leaseDataA.wells.length - leaseDataB.wells.length;
                        break;
                      case 'Flags High to Low':
                        // Use county-lease key for accurate flag counts
                        const countyLeaseKeyA = `${county}-${leaseNumberA}`;
                        const countyLeaseKeyB = `${county}-${leaseNumberB}`;
                        const flagsA = redFlagsCache.get(countyLeaseKeyA)?.totalFlags || 0;
                        const flagsB = redFlagsCache.get(countyLeaseKeyB)?.totalFlags || 0;
                        comparison = flagsB - flagsA;
                        break;
                      case 'Flags Low to High':
                        // Use county-lease key for accurate flag counts
                        const countyLeaseKeyA2 = `${county}-${leaseNumberA}`;
                        const countyLeaseKeyB2 = `${county}-${leaseNumberB}`;
                        const flagsA2 = redFlagsCache.get(countyLeaseKeyA2)?.totalFlags || 0;
                        const flagsB2 = redFlagsCache.get(countyLeaseKeyB2)?.totalFlags || 0;
                        comparison = flagsA2 - flagsB2;
                        break;
                      default:
                        comparison = getFirstName(leaseDataA).localeCompare(getFirstName(leaseDataB));
                    }
                    
                    return comparison;
                  });

                  return sortedLeases.map(([key, leaseData]) => {
                  // Format lease number for display (clean format: 08-12345 or 08-00000)
                  const displayLeaseNumber = formatLeaseNumberForDisplay(
                    leaseData.district || '00',
                    leaseData.leaseNumber
                  );
                  
                  // Check if lease is mapped (has wells with coordinates)
                  const isMapped = leaseData.isMapped || false;
                  const mappedText = !isMapped ? ' Not Mapped' : '';
                  
                  // Get red flags for this lease from cache
                  const defaultFlags = {
                    inactiveWells: false,
                    inactiveWellsOver5Years: false,
                    flaringIntensity: false,
                    fuelGasIntensity: false,
                    inspectionsCompliance: false,
                    violationsCount: false,
                    violationsMajor: false,
                    violationsReinspection: false,
                    severancesCount: false,
                    severancesResolved: false,
                    spillsCount: false,
                    spillsWaterImpact: false
                  };
                  // Use county-lease key for accurate flag counts
                  const countyLeaseKey = `${county}-${key}`;
                  const cached = redFlagsCache.get(countyLeaseKey) || { flags: defaultFlags, totalFlags: 0 };
                  const totalFlags = cached.totalFlags || 0;
                  
                  const tooltipText = `Lease # ${displayLeaseNumber} | ${leaseData.wells.length} well${leaseData.wells.length === 1 ? '' : 's'}${mappedText ? ` | ${mappedText}` : ''}`;
                  
                  return (
                    <Box 
                      key={key} 
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.0 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Checkbox for filtering - only in By Lease view */}
                        {viewMode === 'lease' && (
                          <Checkbox
                            checked={selectedLeaseKeys.has(key)}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLeaseSelection(key);
                            }}
                            size="small"
                          />
                        )}
                <Tooltip title={tooltipText} arrow>
                  <Chip
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                          {Array.isArray(leaseData.leaseName) ? leaseData.leaseName.filter(name => name && name !== 'Unknown Lease').join('; ') || 'Unknown Lease' : leaseData.leaseName}
                        </Typography>
                        {isMapped && (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToLeaseInMap(key, county);
                            }}
                            sx={{ 
                              fontSize: '0.75rem',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            Map
                          </Link>
                        )}
                        {totalFlags > 0 && <FlagIconComponent count={totalFlags} />}
                      </Box>
                    }
                    onClick={() => handleViewLeaseCard(key, leaseData, county)}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      '&:hover': {
                          backgroundColor: 'primary.light'
                      }
                    }}
                  />
                </Tooltip>
                      </Box>
                    
                    {/* Lease details now shown in popup dialog */}
                  </Box>
                );
              });
                })()}
              </Box>
              )}
            </Collapse>
          </Box>
        ));
        })()}
      </Paper>

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              About Report Cards
            </Typography>
            <IconButton
              size="small"
              onClick={() => setAboutDialogOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              The diagrams show aggregated statistics across datasets, grouped by lease, county, or total summary. Each card represents a dataset.
            </Typography>

            <Typography paragraph>
              Lease-level data matches records by lease number. When the lease number is missing, records are grouped by lease name to keep separate leases distinct. Missing values are shown as 'Unknown Lease'.
            </Typography>

            <Typography paragraph>
              <strong>Note:</strong> Well count is based on actual well records. Lease count is based on unique lease keys, derived from the lease number (if available) or the lease name (when the lease number is missing).
            </Typography>

            <Typography paragraph sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FlagIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />
              <strong>Flags</strong>
            </Typography>
            <Typography paragraph>
              Flags indicate potential compliance or operational concerns. Each flag represents a specific metric that exceeds a threshold:
            </Typography>

            <Typography paragraph>
              <strong>Wells</strong>
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              1) <strong>Inactive Total</strong> – Flags when there are any inactive wells (unplugged wells that are not Active or Permit status, or Active/Permit wells with inactive years {'>'} 0).
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              2) <strong>Inactive {'>'}5 Years</strong> – Flags when there are inactive wells that have been inactive for more than 5 years.
            </Typography>

            <Typography paragraph>
              <strong>Production</strong>
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              3) <strong>Flaring Intensity</strong> – Flags when average flaring intensity exceeds 2% of gas produced.
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              4) <strong>Fuel Gas Intensity</strong> – Flags when average fuel gas intensity exceeds 5% of gas produced.
            </Typography>

            <Typography paragraph>
              <strong>Inspections</strong>
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              5) <strong>Compliance Rate</strong> – Flags when the compliance rate is below 90%.
            </Typography>

            <Typography paragraph>
              <strong>Violations</strong>
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              6) <strong>Violations Count</strong> – Flags when there are any violations recorded.
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              7) <strong>Major Violations</strong> – Flags when there are any major violations recorded.
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              8) <strong>Reinspection Compliance</strong> – Flags when the reinspection compliance rate is below 100%.
            </Typography>

            <Typography paragraph>
              <strong>Severances</strong>
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              9) <strong>Severances Count</strong> – Flags when there are any severances recorded.
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              10) <strong>Resolved Rate</strong> – Flags when the severance resolution rate is below 100%.
            </Typography>

            <Typography paragraph>
              <strong>Spills</strong>
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              11) <strong>Spills Count</strong> – Flags when there are any spills recorded.
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              12) <strong>Water Impact</strong> – Flags when there are spills that have impacted water resources.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multiple Lease Card Dialogs - Draggable and outside container */}
      {Array.from(openLeaseCards.entries()).map(([leaseKey, { leaseData, county, size }]) => {
        // Get flag data for this lease (use county-lease key for accuracy)
        const countyLeaseKey = county ? `${county}-${leaseKey}` : leaseKey;
        const cached = redFlagsCache?.get(countyLeaseKey);
        const defaultFlags = {
          inactiveWells: false,
          inactiveWellsOver5Years: false,
          flaringIntensity: false,
          fuelGasIntensity: false,
          inspectionsCompliance: false,
          violationsCount: false,
          violationsMajor: false,
          violationsReinspection: false,
          severancesCount: false,
          severancesResolved: false,
          spillsCount: false,
          spillsWaterImpact: false
        };
        const flags = cached?.flags || defaultFlags;
        
        // Format lease number for display
        const displayLeaseNumber = formatLeaseNumberForDisplay(
          leaseData.district || '00',
          leaseData.leaseNumber
        );
        
        // Get lease name for display
        const leaseName = Array.isArray(leaseData.leaseName) 
          ? leaseData.leaseName.filter(name => name && name !== 'Unknown Lease').join('; ') || 'Unknown Lease'
          : leaseData.leaseName;
        
        return (
          <Dialog
            key={leaseKey}
            open={true}
            onClose={() => handleCloseLeaseCard(leaseKey)}
            container={document.body}
            PaperComponent={DraggablePaper}
            hideBackdrop={true}
            disableEscapeKeyDown={false}
            disableEnforceFocus={true}
            disableAutoFocus={true}
            sx={{
              '& .MuiBackdrop-root': {
                display: 'none', // Remove backdrop completely
              },
              '& .MuiDialog-container': {
                pointerEvents: 'none', // Allow clicks through container
              },
              '& .MuiDialog-root': {
                pointerEvents: 'none', // Allow clicks through root
              },
              '& .MuiDialog-paper': {
                pointerEvents: 'auto', // But allow interaction with dialog itself
                boxShadow: '0px 12px 32px rgba(0,0,0,0.35), 0px 4px 8px rgba(0,0,0,0.2)', // Enhanced shadow for better visibility
                border: '1px solid rgba(0,0,0,0.1)', // Border for definition
                width: size?.width || 800,
                height: size?.height === 'auto' ? 'auto' : (size?.height || 'auto'),

                position: 'relative',
              },
            }}
            slotProps={{
              root: {
                style: {
                  pointerEvents: 'none',
                },
              },
            }}
          >
            <DialogTitle 
              id="draggable-dialog-title" 
              sx={{ 
                cursor: isMobile ? 'default' : 'move',
                py: 1,
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    {leaseName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    Lease # {displayLeaseNumber}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleCloseLeaseCard(leaseKey)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5, px: 1, pb: 0, mt: 1 }}>
              {renderLeaseCardContent(leaseData, flags)}
            </DialogContent>
            {/* Resize Handle - Only on desktop */}
            {!isMobile && (
              <Box
                onMouseDown={(e) => handleResizeStart(e, leaseKey)}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '20px',
                  height: '20px',
                  cursor: 'nwse-resize',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '4px',
                    right: '4px',
                    width: 0,
                    height: 0,
                    borderLeft: '8px solid transparent',
                    borderBottom: '8px solid rgba(0, 0, 0, 0.3)',
                  },
                }}
              />
            )}
          </Dialog>
        );
      })}
    </Box>
  );
};
export default RelationalChart;